import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { persistCandidateForProfile, candidateProfilePath } from "../candidateProfileNav";
import { Plus, Search, Calendar, Brain, Award, Sparkles, Eye, Briefcase, Users, UserCheck, MessageSquare, ExternalLink, Sliders, RefreshCw, Globe, ChevronRight, ChevronLeft, Check, Bell, FileText } from "lucide-react";
import { getTheme, fonts } from "../theme";
import Navbar from "../components/Navbar";
import { Button, Field, inputStyle, ErrorBanner, Select } from "../components/UI";
import api, { recruiterCandidatePhotoUrl } from "../api";

import {
  normalizeOpenPosition,
  isDummyOpenPosition,
  splitSkillList,
  splitSearchValues,
  parseNeedYearList,
  openingSearchBlob,
  openingLocationMatches,
  openingTitleMatches,
  openingYears,
  openingMatchesNeed,
  candidateMatchesOpening,
  stripVerifiedCandidateLabel,
  isPlaceholderCompanyLabel,
  displayPersonName,
  displayCompanyName,
  normalizeLiveCandidate,
  candidatePhotoUrl,
  needsCandidatePhotoProxy,
  candidateDisplayPhotoSrc,
  candidateInitials,
  isNaukriCandidate,
  isNaukriJobListingUrl,
  isLinkedInCandidate,
  linkedInPersonName,
  linkedInCompanyName,
  isGitHubCandidate,
  candidateSourceLabel,
  isDummyCandidate,
  isPeopleCandidate,
  scrapedRecordsFromResult,
  candidateRowId,
  linkedInPeopleSearchUrl,
  linkedInSkillSearchUrl,
  parseRecruiterHeadline,
  indeedPeopleSearchUrl,
  githubPeopleSearchUrl,
  indeedJobsSearchUrl,
  parseLinkedInProfileUrl,
  linkedInSlugFromUrl,
  linkedInContactInfoUrl,
  nameFromLinkedInSlug,
  parseLinkedInContactPaste,
  parseGitHubProfileUrl,
  githubUsernameFromUrl,
  nameFromGitHubUsername,
  parseGitHubContactPaste,
  looksLikeGuessedLinkedInInUrl,
  candidatePublicLink,
  candidateBoardLinks,
  wibilEmployeeIdFromRow,
  namesMatch,
  samePersonName,
  normalizeGivenFamilyName,
  mergeSamePersonCandidates,
  buildLocalCandidateReport,
  persistLocalShortlist,
  SHORTLIST_STORAGE_KEY,
  SHORTLIST_UPDATED_EVENT
} from "../utils/recruiterUtils";

import {
  JOB_BOARDS,
  BOARD_POST_ORDER,
  PRIMARY_JOB_BOARDS,
  EMPTY_JOB_FORM,
  boardComposerUrl,
  boardPastePlaceholder,
  boardInterestCopy,
  validateJobDetails,
  orderedSelectedBoards,
  boardLabel,
  validateJobBoards,
  jobInputStyle,
  FieldError,
  isPostedOnBoard
} from "../components/recruiter/recruiterConstants";

import JobBoardChips from "../components/recruiter/JobBoardChips";
import RecruiterVerificationWizard from "../components/recruiter/RecruiterVerificationWizard";
import RecruiterStatsCards from "../components/recruiter/RecruiterStatsCards";
import PostJobModal from "../components/recruiter/PostJobModal";
import JobsListModal from "../components/recruiter/JobsListModal";
import ShortlistedCandidatesModal from "../components/recruiter/ShortlistedCandidatesModal";
import InterviewFeedbackModal from "../components/recruiter/InterviewFeedbackModal";

export * from "../utils/recruiterUtils";
export * from "../components/recruiter/recruiterConstants";
export { JobBoardChips, RecruiterVerificationWizard, RecruiterStatsCards, PostJobModal, JobsListModal, ShortlistedCandidatesModal, InterviewFeedbackModal };

export default function RecruiterDashboard({ user: propUser, onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("recruiter", themeMode);
  const navigate = useNavigate();

  const sessionUser = (() => {
    if (propUser && propUser.name) return propUser;
    try {
      const raw = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
      if (!raw || raw === "undefined") return propUser || null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : propUser || null;
    } catch (e) {
      return propUser || null;
    }
  })();

  const [profile, setProfile] = useState(null);
  const [profileLoadErr, setProfileLoadErr] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);

  // Forms & Modal state
  const [showJobModal, setShowJobModal] = useState(false);
  const [showJobsListModal, setShowJobsListModal] = useState(false);
  const [showShortlistedModal, setShowShortlistedModal] = useState(false);
  const [shortlistModalFilter, setShortlistModalFilter] = useState("all");
  const [whatsAppMsgStatus, setWhatsAppMsgStatus] = useState("");

  const [jobForm, setJobForm] = useState({ ...EMPTY_JOB_FORM });
  const [jobPostStep, setJobPostStep] = useState(1);
  const [jobFormErrors, setJobFormErrors] = useState({});
  const [boardInterestAsk, setBoardInterestAsk] = useState(null);
  const [boardPublishQueue, setBoardPublishQueue] = useState([]);
  const [boardPublishIndex, setBoardPublishIndex] = useState(0);
  const [hiringRequests, setHiringRequests] = useState([]);
  const [syndicationNotice, setSyndicationNotice] = useState(null);
  const [boardLinkJob, setBoardLinkJob] = useState(null);
  const [boardLinkPlatform, setBoardLinkPlatform] = useState("linkedin");
  const [boardLinkUrl, setBoardLinkUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [pullInputMode, setPullInputMode] = useState("details");
  const [candFirstName, setCandFirstName] = useState("");
  const [candMiddleName, setCandMiddleName] = useState("");
  const [candLastName, setCandLastName] = useState("");
  const [candCompany, setCandCompany] = useState("");
  const [candAgeGroup, setCandAgeGroup] = useState("25-30 Yrs");
  const [candJobTitle, setCandJobTitle] = useState("");
  const [candLocation, setCandLocation] = useState("");
  const [candSkills, setCandSkills] = useState("");
  const [candExperience, setCandExperience] = useState("");
  const [candEmail, setCandEmail] = useState("");
  const [candPhone, setCandPhone] = useState("");
  const [candSummary, setCandSummary] = useState("");
  const [liSaveBusy, setLiSaveBusy] = useState(false);
  const [skillHeadline, setSkillHeadline] = useState("");
  const [showKnownPersonSearch, setShowKnownPersonSearch] = useState(false);
  const [showLiMoreDetails, setShowLiMoreDetails] = useState(false);
  const [showGhMoreDetails, setShowGhMoreDetails] = useState(false);
  const [liCaptureReady, setLiCaptureReady] = useState(false);
  const [liContactCaptureStep, setLiContactCaptureStep] = useState(""); // "", "opened", "ready"
  const liContactOpenedForRef = useRef("");
  const [linkedinPullMessage, setLinkedinPullMessage] = useState("");
  const [linkedinSyncBusy, setLinkedinSyncBusy] = useState(false);
  const [pullBusy, setPullBusy] = useState(false);

  // GitHub Search state
  const [ghFirstName, setGhFirstName] = useState("");
  const [ghMiddleName, setGhMiddleName] = useState("");
  const [ghLastName, setGhLastName] = useState("");
  const [ghUsername, setGhUsername] = useState("");
  const [ghProfileUrl, setGhProfileUrl] = useState("");
  const [ghEmail, setGhEmail] = useState("");
  const [ghPhone, setGhPhone] = useState("");
  const [ghLocation, setGhLocation] = useState("");
  const [ghCompany, setGhCompany] = useState("");
  const [ghJobTitle, setGhJobTitle] = useState("");
  const [ghExperience, setGhExperience] = useState("");
  const [ghSkills, setGhSkills] = useState("");
  const [ghBio, setGhBio] = useState("");
  const [ghSaveBusy, setGhSaveBusy] = useState(false);
  const [ghPullMessage, setGhPullMessage] = useState("");
  const [ghPullBusy, setGhPullBusy] = useState(false);

  // WIBIL Registered Candidate Search state
  const [wibilFirstName, setWibilFirstName] = useState("");
  const [wibilLastName, setWibilLastName] = useState("");
  const [wibilSearchResults, setWibilSearchResults] = useState([]);
  const [wibilSearchBusy, setWibilSearchBusy] = useState(false);
  const [wibilSearchMessage, setWibilSearchMessage] = useState("");

  const [minScore, setMinScore] = useState(300);
  const [candidatePage, setCandidatePage] = useState(1);
  const CANDIDATES_PAGE_SIZE = 8;

  // Selected candidates for "Searched Candidate by Recruiter" list
  const [selectedSearchedCandidates, setSelectedSearchedCandidates] = useState([]);
  const [selectedCandidateKeys, setSelectedCandidateKeys] = useState(new Set());

  // Action Modals & View Actions Popup
  const [viewingCandidateActions, setViewingCandidateActions] = useState(null);
  const [activeScreenerCandidate, setActiveScreenerCandidate] = useState(null);
  const [activeInterviewCandidate, setActiveInterviewCandidate] = useState(null);
  const [activeCertCandidate, setActiveCertCandidate] = useState(null);
  const [modalResumeText, setModalResumeText] = useState("");
  const [modalJobId, setModalJobId] = useState("");
  const [modalScreenerResult, setModalScreenerResult] = useState(null);
  const [modalScreenerBusy, setModalScreenerBusy] = useState(false);
  const [modalInterviewDate, setModalInterviewDate] = useState("");
  const [modalInterviewJobId, setModalInterviewJobId] = useState("");
  const [modalInterviewSuccess, setModalInterviewSuccess] = useState("");

  const getCandidateKey = (c) => {
    return c?.id || c?.profileUrl || c?.github_url || c?.linkedin_url || `${c?.name}-${c?.current_company || c?.title || ""}`;
  };

  const toggleCandidateSelect = async (c) => {
    const key = getCandidateKey(c);
    const alreadySelected = selectedCandidateKeys.has(key);
    if (alreadySelected) {
      setSelectedCandidateKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setSelectedSearchedCandidates((prevList) => prevList.filter((item) => getCandidateKey(item) !== key));
      return;
    }

    setError("");
    let card = normalizeLiveCandidate(c || {});
    const alreadyPersisted = /^\d+$/.test(String(card.id || ""));
    try {
      if (!alreadyPersisted) {
        const isGitHub = isGitHubCandidate(card);
        const res = isGitHub
          ? await api.saveGitHubCandidate({
              firstName: card.firstName,
              middleName: card.middleName,
              lastName: card.lastName,
              name: card.name,
              githubUsername: card.github_username || githubUsernameFromUrl(card.github_url || card.profileUrl),
              email: card.email,
              phone: card.phone,
              location: card.location,
              currentCompany: card.company || card.current_company,
              jobTitle: card.job_title || card.jobTitle || card.role || card.title,
              skills: card.strengths || "",
              bio: card.bio || card.profile_summary || "",
              source: "GITHUB"
            })
          : await api.saveLinkedInCandidate({
              firstName: card.firstName,
              middleName: card.middleName,
              lastName: card.lastName,
              name: card.name,
              linkedinSlug: linkedInSlugFromUrl(card.linkedin_url || card.profileUrl),
              linkedinUsername: linkedInSlugFromUrl(card.linkedin_url || card.profileUrl),
              email: card.email,
              phone: card.phone,
              location: card.location,
              currentCompany: card.company || card.current_company,
              jobTitle: card.job_title || card.jobTitle || card.role || card.title,
              skills: card.strengths || "",
              source: "LINKEDIN"
            });
        if (res?.duplicate && res.candidateId) {
          try {
            const existing = await api.getSourcedCandidate(res.candidateId);
            card = normalizeLiveCandidate({ ...card, ...(existing?.candidate || {}), id: res.candidateId });
          } catch {
            card = normalizeLiveCandidate({ ...card, id: res.candidateId });
          }
        } else if (res?.candidate) {
          card = normalizeLiveCandidate({ ...card, ...res.candidate, id: res.candidate.id || res.candidateId || card.id });
        }
        setSourcedCandidates((prev) => {
          const rest = (prev || []).filter((item) => getCandidateKey(item) !== key);
          return [card, ...rest];
        });
      }
      addCandidateToSearchedList(card);
      if (isGitHubCandidate(card)) setGhPullMessage(`${card.name} saved to Searched Candidate by Recruiter.`);
      else setLinkedinPullMessage(`${card.name} saved to Searched Candidate by Recruiter.`);
      setTimeout(() => matchingCandidatesRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 120);
    } catch (err) {
      setError(err.message || "Could not save the selected candidate.");
    }
  };

  const addCandidateToSearchedList = (c) => {
    if (!c) return;
    const key = getCandidateKey(c);
    setSelectedCandidateKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setSelectedSearchedCandidates((prev) => {
      const idx = prev.findIndex((item) => getCandidateKey(item) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...c };
        return next;
      }
      return [...prev, c];
    });
  };

  const stageAddedCandidate = (card) => {
    if (!card?.name) return card;
    const key = getCandidateKey(card);
    setSourcedCandidates((prev) => {
      const rest = (prev || []).filter((c) => getCandidateKey(c) !== key && String(c.id) !== String(card.id));
      return [card, ...rest];
    });
    return card;
  };

  const isWibilRegistered = (c) => {
    if (!c) return false;
    if (c.wibil_user_id || c.employee_id || c.is_wibil_user || c.is_registered) return true;
    if (c.source === "job_applied" || c.platform === "wibil apply") return true;
    const cName = String(c.name || c.candidate_name || "").toLowerCase().trim();
    if (cName && candidates.some(app => String(app.name || "").toLowerCase().trim() === cName && (app.employee_id || app.id))) {
      return true;
    }
    return false;
  };

  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateReport, setCandidateReport] = useState(null);
  const matchingCandidatesRef = useRef(null);
  const naukriHubRef = useRef(null);
  const candidateDetailRef = useRef(null);
  const linkedinImportRef = useRef(null);
  const githubImportRef = useRef(null);

  // AI resume matcher state
  const [resumeText, setResumeText] = useState("");
  const [targetJobId, setTargetJobId] = useState("");
  const [targetJobIdScreen, setTargetJobIdScreen] = useState("");
  const [matchResult, setMatchResult] = useState(null);

  // Browseract AI Web Scraper state
  const [showAiScraperModal, setShowAiScraperModal] = useState(false);
  const [aiScraperUrl, setAiScraperUrl] = useState("");
  const CANDIDATE_SCRAPE_PROMPT = "Find public candidate profiles: name, current role, company, location, skills";
  const [aiScraperPrompt, setAiScraperPrompt] = useState(CANDIDATE_SCRAPE_PROMPT);
  const [aiScraperType, setAiScraperType] = useState("candidate");
  const [aiScraperBusy, setAiScraperBusy] = useState(false);
  const [aiScraperResult, setAiScraperResult] = useState(null);
  const [showLiJobsModal, setShowLiJobsModal] = useState(false);
  const [liJobsKeywords, setLiJobsKeywords] = useState("Full Stack Developer");
  const [liJobsLocation, setLiJobsLocation] = useState("India");
  const [liJobsLimit, setLiJobsLimit] = useState("10");
  const [liJobsBusy, setLiJobsBusy] = useState(false);
  const [liJobsResult, setLiJobsResult] = useState(null);

  // Interview scheduler state
  const [interviewDate, setInterviewDate] = useState("");
  const [schedulingStatus, setSchedulingStatus] = useState("");

  // Feedback state
  const [selectedLink, setSelectedLink] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackStatus, setFeedbackStatus] = useState("hired");

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [platformFilter, setPlatformFilter] = useState("linkedin");

  // Partner Integrations & Unified Matching Engine State
  const [integrationsList, setIntegrationsList] = useState([]);
  const [recruiterJobTitle, setRecruiterJobTitle] = useState("");
  const [requiredSkillsInput, setRequiredSkillsInput] = useState("");
  const [desiredSkillsInput, setDesiredSkillsInput] = useState("");
  const [expYearsMin, setExpYearsMin] = useState("");
  const [jobLocation, setJobLocation] = useState("");
  const [candidateExperience, setCandidateExperience] = useState("both");
  const [targetCompany, setTargetCompany] = useState("");
  const [careerCandidates, setCareerCandidates] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [selectedOpening, setSelectedOpening] = useState(null);
  const [hubTab, setHubTab] = useState("positions");
  const careerCandidatesRef = useRef(null);
  const jobSeekerProfileRef = useRef(null);
  const openingsFromCareerRef = useRef(false);
  const [isScrapingCareer, setIsScrapingCareer] = useState(false);
  const [isSourcingCandidates, setIsSourcingCandidates] = useState(false);
  const [candidatePullMessage, setCandidatePullMessage] = useState("");
  const [sourcedCandidates, setSourcedCandidates] = useState([]);
  const [isSourcingIndeed, setIsSourcingIndeed] = useState(false);
  const [indeedPullMessage, setIndeedPullMessage] = useState("");
  const [googleApiKey, setGoogleApiKey] = useState("");
  const [googleCx, setGoogleCx] = useState("");
  const [googleCseReady, setGoogleCseReady] = useState(false);
  const [googleCseMessage, setGoogleCseMessage] = useState("");
  const [isSavingCse, setIsSavingCse] = useState(false);
  const [rankedPipeline, setRankedPipeline] = useState(null);
  const [isMatchingJob, setIsMatchingJob] = useState(false);

  const handleConnectIntegration = async (provider) => {
    try {
      const res = await api.connectRecruiterIntegration(provider);
      if (res && res.status) {
        setIntegrationsList(prev => prev.map(p => p.provider === provider ? { ...p, status: res.status.status, message: res.status.message } : p));
      }
    } catch (e) {
      console.error("Connect integration error:", e);
    }
  };

  const mergePulledCandidates = (records) => {
    return (records || [])
      .filter((c) => c && c.name && !c.dummy && !isDummyCandidate(c) && isPeopleCandidate(c))
      .map(normalizeLiveCandidate);
  };

  const liveSourcePayload = (extra = {}) => ({
    title: recruiterJobTitle.trim() || extra.title || "",
    count: extra.count || 10,
    experience: candidateExperience,
    experienceYearsMin: parseNeedYearList(expYearsMin)[0] || 0,
    location: jobLocation.trim() || "India",
    skills: requiredSkillsInput,
    desiredSkills: desiredSkillsInput,
    needText: recruiterJobTitle.trim() || extra.needText || "",
    company: targetCompany.trim() || extra.company || undefined,
    googleApiKey: googleApiKey.trim() || undefined,
    googleCx: googleCx.trim() || undefined,
    platforms: ["naukri", "linkedin", "github"],
    ...extra
  });

  const showSourcedOnRecruiterHub = (pulled) => {
    const people = mergePulledCandidates(pulled);
    setSourcedCandidates(people);
    if (people.some(isNaukriCandidate)) setPlatformFilter("naukri");
    else if (people.length) setPlatformFilter("all");
    setTimeout(() => {
      naukriHubRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return people;
  };

  const pullRequiredCandidates = async (titleOverride, opts = {}) => {
    const title = String(titleOverride || recruiterJobTitle.trim() || selectedOpening?.title || "").trim();
    if (!title) {
      setError("Enter a Target Job Title, then open Job Seeker Profile.");
      return;
    }
    if (opts.activate !== false) setHubTab("jobSeekers");
    setIsSourcingCandidates(true);
    setError("");
    setCandidatePullMessage("");
    try {
      const res = await api.sourceCandidates(liveSourcePayload({
        title,
        needText: title,
        platforms: ["naukri", "linkedin", "github"]
      }));
      const pulled = showSourcedOnRecruiterHub(res?.candidates || []);
      if (res?.error && !pulled.length) {
        setError(res.error);
      } else {
        setError("");
        if (res?.message) setCandidatePullMessage(res.message);
      }
      await loadData();
      if (opts.activate !== false) {
        setTimeout(() => {
          jobSeekerProfileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
      }
    } catch (err) {
      setError(err.message || "Could not fetch job seeker profiles.");
    } finally {
      setIsSourcingCandidates(false);
    }
  };

  const scrapeCompanyCareerSite = async () => {
    const company = targetCompany.trim();
    if (!company) {
      setError("Enter a company name. Fetch uses title, skills, location, and experience to list matching openings.");
      return;
    }
    setIsScrapingCareer(true);
    setError("");
    setCandidatePullMessage("");
    setOpenPositions([]);
    setSelectedOpening(null);
    setCareerCandidates([]);
    openingsFromCareerRef.current = false;
    try {
      const res = await api.scrapeCareerPages(liveSourcePayload({
        title: recruiterJobTitle.trim(),
        company,
        location: jobLocation.trim() || "India",
        experience: candidateExperience,
        experienceYearsMin: String(expYearsMin || "").trim(),
        skills: requiredSkillsInput,
        desiredSkills: desiredSkillsInput,
        count: 40,
        platforms: ["career"]
      }));
      const need = {
        title: recruiterJobTitle.trim(),
        location: jobLocation.trim(),
        experience: candidateExperience,
        minYears: String(expYearsMin || "").trim(),
        skills: requiredSkillsInput
      };
      const raw = (res?.jobs || res?.openPositions || [])
        .map((j, i) => normalizeOpenPosition(j, i))
        .filter((j) => j.title && !isDummyOpenPosition(j));
      const matched = raw.filter((j) => openingMatchesNeed(j, need));
      const openings = matched.length ? matched : raw;
      openingsFromCareerRef.current = openings.length > 0;
      setOpenPositions(openings);
      setSelectedOpening(null);
      setCareerCandidates([]);
      setHubTab("positions");
      loadData().catch(() => { });
      if (res?.error && !openings.length) {
        setError(res.error);
      } else {
        setError("");
        setCandidatePullMessage(res?.message || (openings.length
          ? `Fetched ${openings.length} matching open position(s) for ${company}.`
          : `No matching open positions for ${company} with these filters.`));
      }
      if (openings.length) {
        const seekerTitle = recruiterJobTitle.trim() || openings[0].title;
        pullRequiredCandidates(seekerTitle, { activate: false }).catch(() => { });
        setTimeout(() => {
          careerCandidatesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
      }
    } catch (err) {
      setError(err.message || "Could not fetch open positions for that company.");
    } finally {
      setIsScrapingCareer(false);
    }
  };

  const searchLiveCandidates = async () => {
    const q = searchQuery.trim() || recruiterJobTitle.trim();
    const liveTab = platformFilter === "all" || platformFilter === "linkedin" || platformFilter === "github" || platformFilter === "naukri";
    if (!q || !liveTab) {
      await loadData();
      return;
    }
    setIsSourcingCandidates(true);
    setError("");
    setCandidatePullMessage("");
    try {
      const platforms = platformFilter === "github"
        ? ["github"]
        : platformFilter === "linkedin"
          ? ["linkedin"]
          : platformFilter === "naukri"
            ? ["naukri"]
            : ["naukri", "linkedin", "github"];
      const res = await api.sourceCandidates(liveSourcePayload({
        title: q,
        needText: q,
        platforms
      }));
      const pulled = showSourcedOnRecruiterHub(res?.candidates || []);
      if (res?.error && !pulled.length) {
        setError(res.error);
      } else if (res?.message) {
        setCandidatePullMessage(res.message);
      }
      await loadData();
    } catch (err) {
      setError(err.message || "Could not search live Naukri, LinkedIn, and GitHub candidates.");
    } finally {
      setIsSourcingCandidates(false);
    }
  };

  const pullIndeedJobs = async () => {
    const title = recruiterJobTitle.trim() || searchQuery.trim() || "full stack developer";
    setIsSourcingIndeed(true);
    setError("");
    setIndeedPullMessage("");
    try {
      const res = await api.scrapeIndeedJobs({
        title,
        keywords: title,
        count: 10,
        location: jobLocation.trim() || "India",
        googleApiKey: googleApiKey.trim() || undefined,
        googleCx: googleCx.trim() || undefined
      });
      const records = (res?.candidates || res?.jobs || []).filter((c) => c && (c.name || c.role) && !c.dummy);
      if (res?.error && !records.length) {
        setError(res.error);
      } else if (res?.message) {
        setIndeedPullMessage("Indeed jobs are for job seekers. Recruiter search stays on candidates only. Use Post Job to publish this role.");
      }
      await loadData();
    } catch (err) {
      setError(err.message || "Could not scrape live Indeed jobs.");
    } finally {
      setIsSourcingIndeed(false);
    }
  };

  const runJobMatching = async () => {
    setIsMatchingJob(true);
    try {
      const jobData = {
        title: recruiterJobTitle,
        skills: requiredSkillsInput.split(",").map(s => ({ name: s.trim(), type: "required", weight: 20 })),
        desiredSkills: desiredSkillsInput.split(",").map(s => ({ name: s.trim(), type: "desired", weight: 10 })),
        experienceYearsMin: parseNeedYearList(expYearsMin)[0] || 3,
        location: jobLocation
      };
      const res = await api.matchJobCandidates("1", jobData);
      if (res && res.pipeline) {
        setRankedPipeline(res.pipeline);
      }
    } catch (e) {
      console.error("Run candidate matching error:", e);
    } finally {
      setIsMatchingJob(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      const data = await api.get("/recruiter/analytics");
      setAnalytics(data);

      // Load recruiter partner integrations
      api.getRecruiterIntegrations().then(res => {
        if (res && res.integrations) setIntegrationsList(res.integrations);
      }).catch(() => { });

      // Load jobs
      const jobsListRaw = await api.get("/recruiter/jobs");
      const jobsList = Array.isArray(jobsListRaw) ? jobsListRaw : [];
      setJobs(jobsList);
      if (!openingsFromCareerRef.current) {
        setOpenPositions([]);
      }
      if (jobsList[0]) {
        const firstId = String(jobsList[0].id);
        setTargetJobId(firstId);
        setTargetJobIdScreen(firstId);
      }

      api.getRecruiterHiringRequests().then((rows) => {
        setHiringRequests(Array.isArray(rows) ? rows : []);
      }).catch(() => setHiringRequests([]));

      api.getGoogleCseStatus().then((res) => {
        setGoogleCseReady(Boolean(res?.configured));
        if (res?.message) setGoogleCseMessage(res.message);
      }).catch(() => setGoogleCseReady(false));

      try {
        const list = await api.getAppliedCandidates(searchQuery);
        const applied = mergeSamePersonCandidates(Array.isArray(list) ? list : [])
          .map((c) => normalizeLiveCandidate(c))
          .filter((c) => c && String(c.name || "").trim());
        setCandidates(applied);
      } catch (err) {
        setCandidates([]);
      }
    } catch (err) {
      console.error(err);
    }
  }, [searchQuery]);

  // Load self-profile
  const loadProfile = useCallback(async () => {
    setProfileLoadErr(null);
    try {
      const check = await api.get("/recruiter/profile");
      setProfile(check);
    } catch (err) {
      setProfile(null);
      setProfileLoadErr(err.message || "Failed to load recruiter profile.");
    }
  }, []);

  useEffect(() => {
    loadProfile();
    loadData();
  }, [loadProfile, loadData]);

  useEffect(() => {
    const onSync = () => {
      loadData();
    };
    window.addEventListener("storage", onSync);
    window.addEventListener("focus", onSync);
    return () => {
      window.removeEventListener("storage", onSync);
      window.removeEventListener("focus", onSync);
    };
  }, [loadData]);

  useEffect(() => {
    if (window.location.hash !== "#matching-candidates") return;
    const t = setTimeout(() => {
      matchingCandidatesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [profile]);

  const openJobModal = () => {
    setJobForm({
      ...EMPTY_JOB_FORM,
      role: recruiterJobTitle.trim(),
      skills: requiredSkillsInput.trim(),
      location: jobLocation.trim(),
      experience: expYearsMin ? `${expYearsMin}+ years` : ""
    });
    setJobPostStep(1);
    setJobFormErrors({});
    setBoardInterestAsk(null);
    setBoardPublishQueue([]);
    setBoardPublishIndex(0);
    setError("");
    setShowJobModal(true);
  };

  const closeJobModal = () => {
    setShowJobModal(false);
    setJobPostStep(1);
    setJobFormErrors({});
    setBoardInterestAsk(null);
    setBoardPublishQueue([]);
    setBoardPublishIndex(0);
  };

  const openOneBoardWindow = async (job, platform) => {
    const text = jobPostText(job);
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) { }
    const url = boardComposerUrl(platform);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setBoardLinkJob(job);
    setBoardLinkPlatform(platform);
  };

  const finishBoardPublish = () => {
    const labels = boardPublishQueue.map(boardLabel);
    setSyndicationNotice({
      message: labels.length
        ? `Job saved. Posted one by one: ${labels.join(" → ")}.`
        : "Job saved on Wibil.",
      job: boardLinkJob,
      applyUrl: boardLinkJob?.apply_url
    });
    setJobForm({ ...EMPTY_JOB_FORM });
    closeJobModal();
    loadData();
  };

  const goToNextBoard = async ({ skip } = {}) => {
    const currentId = boardPublishQueue[boardPublishIndex];
    if (!skip && boardLinkUrl.trim() && boardLinkJob?.id && currentId) {
      try {
        await api.saveJobBoardLink(boardLinkJob.id, currentId, boardLinkUrl.trim());
      } catch (err) {
        setError(err.message);
        return;
      }
    }
    const next = boardPublishIndex + 1;
    setBoardLinkUrl("");
    setError("");
    if (next >= boardPublishQueue.length) {
      finishBoardPublish();
      return;
    }
    const nextId = boardPublishQueue[next];
    setBoardPublishIndex(next);
    await openOneBoardWindow(boardLinkJob, nextId);
  };

  const handlePostJob = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const detailErrors = validateJobDetails(jobForm);
    const boardErrors = validateJobBoards(jobForm);
    const errors = { ...detailErrors, ...boardErrors };
    setJobFormErrors(errors);
    if (Object.keys(detailErrors).length) {
      setJobPostStep(1);
      return;
    }
    if (Object.keys(boardErrors).length) {
      setJobPostStep(2);
      return;
    }
    setError("");
    setBusy(true);
    try {
      const platforms = orderedSelectedBoards(jobForm.platforms || []);
      const data = await api.post("/recruiter/jobs", {
        ...jobForm,
        openings: Number(jobForm.openings || 1),
        platforms,
        hiring_request_id: jobForm.hiring_request_id || undefined
      });
      const savedJob = data?.job || { ...jobForm, id: data?.jobId, apply_url: data?.applyUrl };
      setBoardLinkJob(savedJob);
      setBoardPublishQueue(platforms);
      setBoardPublishIndex(0);
      setBoardLinkUrl("");
      setJobPostStep(3);
      setSyndicationNotice(null);
      loadData();
      if (platforms[0]) {
        await openOneBoardWindow(savedJob, platforms[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onJobFormSubmit = (e) => {
    e.preventDefault();
    if (jobPostStep === 1) {
      const errors = validateJobDetails(jobForm);
      setJobFormErrors(errors);
      if (Object.keys(errors).length) return;
      setJobPostStep(2);
      return;
    }
    if (jobPostStep === 2) {
      handlePostJob(e);
    }
  };

  const postHiringRequest = async (request) => {
    setError(""); setBusy(true);
    try {
      const data = await api.postHiringRequestJob(request.id);
      const savedJob = data?.job || { role: request.role, company_name: request.employer_company, location: request.location, experience: request.experience, salary_range: request.salary_range, skills: request.skills, apply_url: data?.applyUrl, id: data?.jobId };
      setSyndicationNotice(data);
      const text = jobPostText(savedJob);
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) { }
      const openBoards = Array.isArray(data?.openBoards) && data.openBoards.length
        ? data.openBoards
        : [
          { platform: "indeed", composerUrl: boardComposerUrl("indeed") },
          { platform: "linkedin", composerUrl: boardComposerUrl("linkedin") }
        ];
      openBoards.forEach((board) => {
        if (board.composerUrl) window.open(board.composerUrl, "_blank", "noopener,noreferrer");
      });
      if (openBoards[0]) {
        setBoardLinkJob(savedJob);
        setBoardLinkPlatform(openBoards[0].platform);
        setBoardLinkUrl("");
      }
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleJobPlatform = (id) => {
    const board = JOB_BOARDS.find((b) => b.id === id);
    const alreadyOn = jobForm.platforms.includes(id);
    if (!alreadyOn && board?.needsInterest) {
      setBoardInterestAsk(board);
      return;
    }
    setJobForm((prev) => {
      const on = prev.platforms.includes(id);
      return { ...prev, platforms: on ? prev.platforms.filter((p) => p !== id) : [...prev.platforms, id] };
    });
    setJobFormErrors((prev) => {
      const next = { ...prev };
      delete next.platforms;
      return next;
    });
  };

  const confirmBoardInterest = (interested) => {
    const board = boardInterestAsk;
    setBoardInterestAsk(null);
    if (!board) return;
    setJobForm((prev) => {
      const without = prev.platforms.filter((p) => p !== board.id);
      if (!interested) return { ...prev, platforms: without };
      return { ...prev, platforms: [...without, board.id] };
    });
    setJobFormErrors((prev) => {
      const next = { ...prev };
      delete next.platforms;
      return next;
    });
  };

  const jobPostText = (job) => [
    job.role,
    job.company_name || profile?.company_name || "",
    job.location || "Chennai",
    `Experience: ${job.experience}`,
    `Salary: ${job.salary_range}`,
    `Skills: ${job.skills}`,
    job.description || "",
    `Apply: ${job.apply_url || `https://wibil.co/jobs/${job.public_slug || job.id}`}`
  ].filter(Boolean).join("\n");

  const openBoardToPost = async (job, platform) => {
    const text = jobPostText(job);
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) { }
    const url = boardComposerUrl(platform);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setBoardLinkJob(job);
    setBoardLinkPlatform(platform);
    setBoardLinkUrl("");
    const label = platform === "indeed" ? "Indeed" : platform === "linkedin" ? "LinkedIn" : "Naukri";
    setSyndicationNotice({
      message: `Job details copied. ${label} posting opened. After it publishes, paste the live ${label} job URL below.`,
      job,
      applyUrl: job.apply_url
    });
  };

  const saveBoardLink = async (e) => {
    e.preventDefault();
    if (!boardLinkJob) return;
    setBusy(true);
    setError("");
    try {
      await api.saveJobBoardLink(boardLinkJob.id, boardLinkPlatform, boardLinkUrl);
      setBoardLinkUrl("");
      setBoardLinkJob(null);
      await loadData();
      setSyndicationNotice({ message: `Saved. Job is now marked posted on ${boardLinkPlatform === "linkedin" ? "LinkedIn" : "Naukri"}.` });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadSavedLiveCandidates = useCallback(async () => {
    try {
      const [liRes, ghRes] = await Promise.all([
        api.listSourcedCandidates({ source: "LINKEDIN", page: 1, limit: 50, sort: "newest" }),
        api.listSourcedCandidates({ source: "GITHUB", page: 1, limit: 50, sort: "newest" })
      ]);
      const cards = [
        ...(liRes?.candidates || []),
        ...(ghRes?.candidates || [])
      ].map(normalizeLiveCandidate).filter(isPeopleCandidate);
      setSelectedSearchedCandidates(cards);
      setSelectedCandidateKeys(new Set(cards.map((c) => getCandidateKey(c))));
      // Added candidates stay session-only (cleared by refresh / new page load).
    } catch (err) {
      /* recruiter can still stage candidates without saved records */
    }
  }, []);

  useEffect(() => {
    loadSavedLiveCandidates();
  }, [loadSavedLiveCandidates]);

  const pullLinkedInProfile = async () => {
    const first = candFirstName.trim();
    const middle = candMiddleName.trim();
    const last = candLastName.trim();
    const company = candCompany.trim();
    const location = candLocation.trim();
    const role = candJobTitle.trim() || skillHeadline.trim();
    const ageGroup = candAgeGroup.trim();
    if (!first && !last && !company) {
      setError("Enter First Name and Last Name, or Company Name. Middle Name is optional.");
      return;
    }
    const displayName = [first, middle, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim()
      || (company ? `People at ${company}` : "LinkedIn search");
    const payload = {
      firstName: first,
      middleName: middle,
      lastName: last,
      company,
      location,
      jobTitle: role,
      ageGroup,
      name: displayName
    };

    setPullBusy(true);
    setError("");
    setLinkedinPullMessage("");
    try {
      const data = await api.pullLinkedInCandidate(payload);
      const apiCand = data?.candidate || {};
      const filledFirst = first || String(apiCand.firstName || "").trim();
      const filledLast = last || String(apiCand.lastName || "").trim();
      const filledCompany = company || String(apiCand.current_company || apiCand.company || "").trim();
      const filledLocation = location || String(apiCand.location || "").trim();
      const filledRole = role || String(apiCand.title || apiCand.job_title || apiCand.jobTitle || "").trim();
      if (filledFirst) setCandFirstName(filledFirst);
      if (filledLast) setCandLastName(filledLast);
      if (filledCompany) setCandCompany(filledCompany);
      if (filledLocation) setCandLocation(filledLocation);
      if (filledRole) setCandJobTitle(filledRole);
      setLiCaptureReady(true);
      const card = normalizeLiveCandidate({
        ...apiCand,
        firstName: filledFirst,
        middleName: middle,
        lastName: filledLast,
        name: displayName,
        company: filledCompany,
        current_company: filledCompany,
        title: filledRole || filledCompany,
        location: filledLocation,
        ageGroup,
        platform: "LinkedIn",
        badgeText: "LinkedIn",
        source: apiCand.source || "linkedin_details_input",
        kind: "person"
      });
      card.profileUrl = linkedInPeopleSearchUrl(card);
      card.linkedin_url = card.profileUrl;
      setSourcedCandidates((prev) => {
        const key = `${displayName.toLowerCase()}|${filledCompany.toLowerCase()}`;
        const rest = (prev || []).filter((c) => `${linkedInPersonName(c).toLowerCase()}|${linkedInCompanyName(c).toLowerCase()}` !== key);
        return [card, ...rest];
      });
      setPlatformFilter("linkedin");
      setLinkedinPullMessage(
        `Filled Add contact: ${[filledFirst, filledLast].filter(Boolean).join(" ")}` +
        (filledCompany || filledLocation || filledRole
          ? ` · ${[filledRole, filledCompany, filledLocation].filter(Boolean).join(" · ")}`
          : "") +
        ". Paste the profile URL, then add email/phone."
      );
      setTimeout(() => linkedinImportRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setPullBusy(false);
    }
  };

  const openLinkedInSkillSearch = () => {
    const headline = skillHeadline.trim();
    if (!headline) {
      setError("Paste a role, company, or skills headline to open LinkedIn search.");
      return;
    }
    setError("");
    window.open(linkedInSkillSearchUrl(parseRecruiterHeadline(headline)), "_blank", "noopener,noreferrer");
    setLiCaptureReady(true);
    setLinkedinPullMessage("LinkedIn is open. Open a candidate, copy the profile URL, then paste it in step 2.");
    setTimeout(() => linkedinImportRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
  };

  const applyLinkedInContactPaste = (text, overwriteName = true) => {
    const parsed = parseLinkedInContactPaste(text);
    if (parsed.url) setLinkedinUrl(parsed.url);
    if (parsed.email) setCandEmail(parsed.email);
    if (parsed.phone) setCandPhone(parsed.phone);
    if (parsed.firstName) setCandFirstName((prev) => (overwriteName || !String(prev).trim()) ? parsed.firstName : prev);
    if (parsed.lastName) setCandLastName((prev) => (overwriteName || !String(prev).trim()) ? parsed.lastName : prev);
    return parsed;
  };

  /** Open LinkedIn Contact info overlay for the listed profile (uses recruiter's LinkedIn session). */
  const openLinkedInContactInfo = (rawUrl = linkedinUrl, { auto = false } = {}) => {
    const profileUrl = parseLinkedInProfileUrl(rawUrl) || (linkedInSlugFromUrl(rawUrl) ? `https://www.linkedin.com/in/${linkedInSlugFromUrl(rawUrl)}/` : "");
    const contactUrl = linkedInContactInfoUrl(profileUrl || rawUrl);
    if (!contactUrl) {
      if (!auto) {
        setError("Paste a LinkedIn profile URL (linkedin.com/in/...) first.");
        setLinkedinPullMessage("Paste the profile URL, then open Contact info.");
      }
      return false;
    }
    if (auto && liContactOpenedForRef.current === contactUrl) return true;
    liContactOpenedForRef.current = contactUrl;
    setLinkedinUrl(profileUrl || linkedinUrl);
    applyLinkedInContactPaste(profileUrl || rawUrl, true);
    setLiCaptureReady(true);
    setLiContactCaptureStep("opened");
    window.open(contactUrl, "_blank", "noopener,noreferrer");
    setLinkedinPullMessage(
      "Contact info opened on LinkedIn. Copy email and mobile from that panel, return here, then click Paste contact & add."
    );
    return true;
  };

  const onLinkedInProfileListed = (text, { openContact = true, overwriteName = true } = {}) => {
    const parsed = applyLinkedInContactPaste(text, overwriteName);
    setLiCaptureReady(true);
    if (parsed.url && openContact) {
      openLinkedInContactInfo(parsed.url, { auto: true });
    } else if (parsed.email || parsed.phone) {
      setLiContactCaptureStep("ready");
      setLinkedinPullMessage("Email/phone captured from Contact info. Click Add to list (or Paste contact & add).");
    }
    return parsed;
  };

  /** Clipboard → parse Contact info → fill email/phone → stage into Added candidates. */
  const pasteLinkedInContactAndAdd = async () => {
    setError("");
    try {
      const text = await navigator.clipboard.readText();
      if (!String(text || "").trim()) {
        setError("Clipboard is empty. On LinkedIn Contact info, copy the email/phone text, then click Paste contact & add.");
        return;
      }
      const mergedText = [linkedinUrl, text].filter(Boolean).join("\n");
      const parsed = onLinkedInProfileListed(mergedText, { openContact: false, overwriteName: true });
      const hasUrl = !!(parsed.url || parseLinkedInProfileUrl(linkedinUrl) || linkedInSlugFromUrl(linkedinUrl));
      if (!hasUrl) {
        setLinkedinPullMessage("Contact details found. Paste the LinkedIn profile URL first, then Add to list.");
        return;
      }
      if (!parsed.email && !parsed.phone) {
        setLinkedinPullMessage("No email/phone found in the paste. Open Contact info, copy the email and mobile lines, then try again.");
        setLiContactCaptureStep("opened");
        return;
      }
      setLiContactCaptureStep("ready");
      await saveLinkedInCandidate({
        linkedinUrl: parsed.url || linkedinUrl,
        firstName: parsed.firstName || candFirstName,
        lastName: parsed.lastName || candLastName,
        email: parsed.email || candEmail,
        phone: parsed.phone || candPhone,
        jobTitle: candJobTitle,
        currentCompany: candCompany,
        location: candLocation
      });
      setLinkedinPullMessage(
        `Added with ${[parsed.email || candEmail, parsed.phone || candPhone].filter(Boolean).join(" · ")}. Check the box to save into Searched Candidate by Recruiter.`
      );
    } catch {
      setError("Allow clipboard access, or paste Contact info into the URL field, then click Add to list.");
    }
  };

  const pasteLinkedInContact = async () => {
    setError("");
    try {
      const text = await navigator.clipboard.readText();
      if (!String(text || "").trim()) {
        setError("Clipboard is empty. Copy the LinkedIn profile URL or Contact info, then click Paste.");
        return;
      }
      const parsedOnce = parseLinkedInContactPaste(text);
      const parsed = onLinkedInProfileListed(text, {
        openContact: !!(parsedOnce.url && !(parsedOnce.email || parsedOnce.phone)),
        overwriteName: true
      });
      if (!parsed.url && !parsed.email && !parsed.phone) {
        setLinkedinUrl(String(text).trim());
        setLinkedinPullMessage("Pasted. If this is a profile URL, Contact info will open next.");
        return;
      }
      if (parsed.url && (parsed.email || parsed.phone)) {
        setLiContactCaptureStep("ready");
        setLinkedinPullMessage("Name and contact filled. Click Add to list.");
        return;
      }
      if (parsed.email || parsed.phone) {
        setLiContactCaptureStep("ready");
        setLinkedinPullMessage("Contact details filled. Paste the LinkedIn profile URL if missing, then Add to list.");
      }
    } catch {
      setError("Allow clipboard access, or paste the LinkedIn profile URL into the field.");
    }
  };

  const applyGitHubContactPaste = (text, overwriteName = true) => {
    const parsed = parseGitHubContactPaste(text);
    if (parsed.url) setGhProfileUrl(parsed.url);
    if (parsed.username) setGhUsername(parsed.username);
    if (parsed.email) setGhEmail(parsed.email);
    if (parsed.phone) setGhPhone(parsed.phone);
    if (parsed.firstName) setGhFirstName((prev) => (overwriteName || !String(prev).trim()) ? parsed.firstName : prev);
    if (parsed.lastName) setGhLastName((prev) => (overwriteName || !String(prev).trim()) ? parsed.lastName : prev);
    return parsed;
  };

  const pasteGitHubContact = async () => {
    setError("");
    try {
      const text = await navigator.clipboard.readText();
      if (!String(text || "").trim()) {
        setError("Clipboard is empty. Copy the GitHub profile URL, then click Paste.");
        return;
      }
      const parsed = applyGitHubContactPaste(text, true);
      if (!parsed.url && !parsed.username && !parsed.email && !parsed.phone) {
        setGhProfileUrl(String(text).trim());
        setGhPullMessage("Pasted. If this is a GitHub profile URL, click Add to list.");
        return;
      }
      if (parsed.url || parsed.username) {
        setGhPullMessage(parsed.email || parsed.phone
          ? "Name and contact filled. Click Add to list."
          : "Username filled from the URL. Type email/phone if public, then click Add to list.");
        return;
      }
      setGhPullMessage("Contact details filled. Paste the GitHub profile URL, then click Add to list.");
    } catch {
      setError("Allow clipboard access, or paste the GitHub profile URL into the field.");
    }
  };

  const pullGitHubProfile = async () => {
    const first = ghFirstName.trim();
    const middle = ghMiddleName.trim();
    const last = ghLastName.trim();
    const company = ghCompany.trim();
    const location = ghLocation.trim();
    const role = ghJobTitle.trim();
    if (!first || !last) {
      setError("Enter GitHub candidate First Name and Last Name.");
      return;
    }
    const displayName = [first, middle, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    const searchName = `${first} ${last}`.replace(/\s+/g, " ").trim();
    const searchUrl = githubPeopleSearchUrl({ firstName: first, lastName: last, name: searchName });
    setGhPullBusy(true);
    setError("");
    setGhPullMessage("");
    try {
      let users = [];
      try {
        const ghRes = await fetch(`https://api.github.com/search/users?q=${encodeURIComponent(searchName)}+in:name&per_page=10`);
        if (ghRes.ok) {
          const json = await ghRes.json();
          users = json.items || [];
        }
      } catch (e) {
        // Fallback silently if rate-limited
      }

      if (role) setGhJobTitle(role);
      if (company) setGhCompany(company);
      if (location) setGhLocation(location);
      setGhFirstName(first);
      setGhLastName(last);

      if (users.length > 0) {
        const cards = users.map((u) => normalizeLiveCandidate({
          id: `gh-${u.id}`,
          name: displayName,
          firstName: first,
          middleName: middle,
          lastName: last,
          current_company: company || `@${u.login}`,
          company: company || `@${u.login}`,
          title: role || `@${u.login}`,
          role: role || `@${u.login}`,
          job_title: role,
          location,
          platform: "GitHub",
          badgeText: "GitHub",
          profileUrl: u.html_url || `https://github.com/${u.login}`,
          github_url: u.html_url || `https://github.com/${u.login}`,
          github_username: u.login,
          source: "github_search",
          avatar_url: u.avatar_url,
          kind: "person"
        }));
        setSourcedCandidates((prev) => {
          const prevList = prev || [];
          const existingUrls = new Set(prevList.map((c) => c.profileUrl || c.name));
          const fresh = cards.filter((c) => !existingUrls.has(c.profileUrl));
          return [...fresh, ...prevList];
        });
        if (users[0]?.login) {
          setGhUsername(users[0].login);
          setGhProfileUrl(users[0].html_url || `https://github.com/${users[0].login}`);
        }
        setGhPullMessage(
          `Filled Add contact: ${displayName}` +
          (role || company || location ? ` · ${[role, company, location].filter(Boolean).join(" · ")}` : "") +
          `. Found ${cards.length} GitHub profile(s). Paste URL if needed, then Add to list.`
        );
      } else {
        window.open(searchUrl, "_blank", "noopener,noreferrer");
        setGhPullMessage(
          `Filled Add contact: ${displayName}` +
          (role || company || location ? ` · ${[role, company, location].filter(Boolean).join(" · ")}` : "") +
          ". GitHub opened — copy a profile URL into step 2."
        );
      }
      setPlatformFilter("github");
      setTimeout(() => githubImportRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
      await loadData();
    } catch (err) {
      setError(err.message || "Failed to search GitHub profiles.");
    } finally {
      setGhPullBusy(false);
    }
  };

  const saveGitHubCandidate = async () => {
    const parsedUrl = parseGitHubProfileUrl(ghProfileUrl) || (ghUsername.trim() ? `https://github.com/${ghUsername.trim()}` : "");
    const username = githubUsernameFromUrl(parsedUrl || ghUsername) || ghUsername.trim();
    const fromUser = nameFromGitHubUsername(username);
    const first = ghFirstName.trim() || fromUser.firstName;
    const middle = ghMiddleName.trim();
    const last = ghLastName.trim() || fromUser.lastName;
    const role = ghJobTitle.trim();
    const company = ghCompany.trim();
    const location = ghLocation.trim();
    const email = ghEmail.trim();
    const phone = ghPhone.trim();
    const displayName = [first, middle, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim() || fromUser.name || username;
    if (!displayName) {
      setError("Enter GitHub candidate First Name and Last Name, or paste a profile URL.");
      return;
    }
    if (!username) {
      setError("Paste a public GitHub profile URL or username to save the candidate.");
      return;
    }
    setError("");
    const card = normalizeLiveCandidate({
      id: `gh-${username}`,
      name: displayName,
      firstName: first,
      middleName: middle,
      lastName: last,
      email,
      phone,
      location,
      current_company: company,
      company,
      company_name: company,
      title: role,
      role,
      job_title: role,
      jobTitle: role,
      github_url: parsedUrl || `https://github.com/${username}`,
      profileUrl: parsedUrl || `https://github.com/${username}`,
      github_username: username,
      strengths: ghSkills.trim(),
      bio: ghBio.trim(),
      source: "GITHUB",
      platform: "GitHub",
      kind: "person"
    });
    stageAddedCandidate(card);
    setPlatformFilter("github");
    setGhProfileUrl("");
    setGhUsername("");
    setGhEmail("");
    setGhPhone("");
    setGhFirstName("");
    setGhMiddleName("");
    setGhLastName("");
    setGhJobTitle("");
    setGhCompany("");
    setGhLocation("");
    setGhSkills("");
    setGhPullMessage(`${displayName} added. Check the box to save into Searched Candidate by Recruiter.`);
  };

  const saveLinkedInCandidate = async (overrides = {}) => {
    const urlValue = String(overrides.linkedinUrl != null ? overrides.linkedinUrl : linkedinUrl).trim();
    const parsedUrl = parseLinkedInProfileUrl(urlValue) || (linkedInSlugFromUrl(urlValue) ? `https://www.linkedin.com/in/${linkedInSlugFromUrl(urlValue)}/` : "");
    const slug = linkedInSlugFromUrl(parsedUrl || urlValue);
    if (!parsedUrl || !slug) {
      const msg = "Paste the opened candidate LinkedIn URL (linkedin.com/in/...) from the address bar or Contact info.";
      setError(msg);
      setLinkedinPullMessage(msg);
      return;
    }
    const fromSlug = nameFromLinkedInSlug(parsedUrl);
    const first = String(overrides.firstName != null ? overrides.firstName : candFirstName).trim() || fromSlug.firstName;
    const middle = String(overrides.middleName != null ? overrides.middleName : candMiddleName).trim();
    const last = String(overrides.lastName != null ? overrides.lastName : candLastName).trim() || fromSlug.lastName;
    const email = String(overrides.email != null ? overrides.email : candEmail).trim();
    const phone = String(overrides.phone != null ? overrides.phone : candPhone).trim();
    const role = String(overrides.jobTitle != null ? overrides.jobTitle : candJobTitle).trim() || skillHeadline.trim();
    const company = String(overrides.currentCompany != null ? overrides.currentCompany : candCompany).trim();
    const location = String(overrides.location != null ? overrides.location : candLocation).trim();
    const displayName = [first, middle, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim() || fromSlug.name;
    if (!displayName) {
      const msg = "Enter the candidate name shown on LinkedIn, or paste a profile URL so we can fill it.";
      setError(msg);
      setLinkedinPullMessage(msg);
      return;
    }
    setError("");
    const card = normalizeLiveCandidate({
      id: `li-${parsedUrl}`,
      name: displayName,
      firstName: first,
      lastName: last,
      middleName: middle,
      email,
      phone,
      title: role,
      role,
      job_title: role,
      jobTitle: role,
      current_company: company,
      company,
      company_name: company,
      location,
      linkedin_url: parsedUrl,
      profileUrl: parsedUrl,
      strengths: candSkills.trim(),
      source: "LINKEDIN",
      platform: "LinkedIn",
      kind: "person"
    });
    stageAddedCandidate(card);
    setPlatformFilter("linkedin");
    setLinkedinUrl("");
    setCandFirstName("");
    setCandMiddleName("");
    setCandLastName("");
    setCandEmail("");
    setCandPhone("");
    setCandJobTitle("");
    setCandCompany("");
    setCandLocation("");
    setCandSkills("");
    setLiCaptureReady(true);
    setLinkedinPullMessage(`${displayName} added. Check the box to save into Searched Candidate by Recruiter.`);
  };

  const searchSavedLiveCandidates = async () => {
    setError("");
    try {
      const res = await api.listSourcedCandidates({
        source: platformFilter === "github" ? "GITHUB" : "LINKEDIN",
        name: searchQuery,
        skills: platformFilter === "github" ? ghSkills : candSkills,
        location: platformFilter === "github" ? ghLocation : candLocation,
        experience: platformFilter === "github" ? ghExperience : candExperience,
        company: platformFilter === "github" ? ghCompany : candCompany,
        jobTitle: platformFilter === "github" ? ghJobTitle : candJobTitle,
        githubUsername: ghUsername,
        linkedinUrl: linkedinUrl,
        page: 1,
        limit: 20,
        sort: "newest"
      });
      const cards = (res?.candidates || []).map(normalizeLiveCandidate).filter(isPeopleCandidate);
      setSelectedSearchedCandidates(cards);
      setSelectedCandidateKeys(new Set(cards.map((c) => getCandidateKey(c))));
      const msg = cards.length ? `Loaded ${res.total || cards.length} saved candidate(s).` : "No saved candidates match these filters.";
      if (platformFilter === "github") setGhPullMessage(msg);
      else setLinkedinPullMessage(msg);
      setTimeout(() => matchingCandidatesRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 120);
    } catch (err) {
      setError(err.message || "Could not search saved candidates.");
    }
  };

  const syncLinkedInExports = async () => {
    setLinkedinSyncBusy(true);
    setError("");
    setLinkedinPullMessage("");
    try {
      const res = await api.syncRecruiterIntegration("linkedin_rsc");
      const count = res?.syncResult?.recordsProcessed || res?.syncResult?.candidates?.length || 0;
      setLinkedinPullMessage(res?.syncResult?.message || (count ? `Synced ${count} LinkedIn Recruiter export(s).` : "No new LinkedIn Recruiter exports."));
      setPlatformFilter("linkedin");
      await loadData();
    } catch (err) {
      setError(err.message || "LinkedIn Recruiter sync failed.");
    } finally {
      setLinkedinSyncBusy(false);
    }
  };

  const runAiWebScraper = async (e) => {
    e.preventDefault();
    if (!aiScraperUrl.trim()) return;
    setAiScraperBusy(true);
    setAiScraperResult(null);
    setError("");
    try {
      const res = await api.scrapeWebsiteWithAi({
        targetUrl: aiScraperUrl.trim(),
        prompt: aiScraperPrompt,
        extractType: aiScraperType || "auto",
        saveToDb: true
      });
      const records = scrapedRecordsFromResult(res).filter((c) => c && c.name && !c.dummy && isPeopleCandidate(c));
      setAiScraperResult({ ...res, records });
      if (res?.error && !res?.extractedData && !records.length) {
        setError(res.error);
      }
      await loadData();
      if (res?.ingestNotice) {
        setCandidatePullMessage(res.ingestNotice);
      } else if (records.length) {
        setCandidatePullMessage(`Scraped ${records.length} live web profile(s). Dummy people are not shown.`);
      }
    } catch (err) {
      setError(err.message || "Failed to scrape website with AI.");
    } finally {
      setAiScraperBusy(false);
    }
  };

  const runLinkedInJobScraper = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLiJobsBusy(true);
    setLiJobsResult(null);
    setError("");
    try {
      const res = await api.scrapeLinkedInJobs({
        keywords: (liJobsKeywords || recruiterJobTitle || "Full Stack Developer").trim(),
        location: (liJobsLocation || jobLocation || "India").trim(),
        limit: Math.min(15, Math.max(1, Number(liJobsLimit) || 10))
      });
      setLiJobsResult(res);
      if (res?.error && !(res?.jobs || []).length) {
        setError(res.error);
      }
      await loadData();
    } catch (err) {
      setError(err.message || "Failed to scrape LinkedIn jobs.");
      setLiJobsResult({ success: false, dummy: false, jobs: [], error: err.message });
    } finally {
      setLiJobsBusy(false);
    }
  };

  const searchWibilCandidates = async () => {
    const fn = wibilFirstName.trim();
    const ln = wibilLastName.trim();
    const parsed = normalizeGivenFamilyName(fn, ln);
    const query = parsed.identityName || [fn, ln].filter(Boolean).join(" ").trim();
    if (!query) {
      setError("Please enter First Name or Last Name to search WIBIL registered candidates.");
      return;
    }
    setError("");
    setWibilSearchBusy(true);
    setWibilSearchMessage("");
    try {
      const res = await api.getAppliedCandidates(query);
      const list = mergeSamePersonCandidates(Array.isArray(res) ? res : (Array.isArray(res?.candidates) ? res.candidates : []));
      const matched = list.filter(c => {
        const cName = String(c.name || c.candidate_name || "");
        const cEmail = String(c.email || "").toLowerCase();
        const qLower = query.toLowerCase();
        if (samePersonName(cName, parsed.displayName || query)) return true;
        if (fn && ln && samePersonName(cName, `${fn} ${ln}`)) return true;
        if (cEmail.includes(qLower)) return true;
        return false;
      });

      if (matched.length > 0) {
        setWibilSearchResults(matched);
        setWibilSearchMessage(`Found ${matched.length} registered candidate(s) in WIBIL.`);
      } else {
        if (list.length > 0) {
          setWibilSearchResults(list);
          setWibilSearchMessage(`Showing ${list.length} registered candidate(s) in WIBIL.`);
        } else {
          setWibilSearchResults([]);
          setWibilSearchMessage(`No registered candidates found matching "${query}".`);
        }
      }
    } catch (err) {
      setError(err.message || "Failed to search WIBIL candidates.");
    } finally {
      setWibilSearchBusy(false);
    }
  };

  const openCandidateProfile = (candidateOrId) => {
    const clicked = candidateOrId && typeof candidateOrId === "object"
      ? { ...candidateOrId }
      : { id: candidateOrId, name: "" };
    const rowId = candidateRowId(clicked);
    if (!rowId && !clicked.name) {
      setError("This candidate has no profile id.");
      return;
    }
    persistCandidateForProfile(clicked);
    navigate(candidateProfilePath(clicked), { state: { candidate: clicked } });
  };

  const viewCandidateDetails = openCandidateProfile;

  // Run AI Resume Matcher
  const runResumeScreening = async (e) => {
    e.preventDefault();
    if (!resumeText || !targetJobIdScreen) return;
    setError(""); setBusy(true);
    try {
      const selectedJob = jobs.find(j => j.id === parseInt(targetJobIdScreen, 10));
      const skillsArray = selectedJob ? selectedJob.skills.split(",") : [];
      const screening = await api.post("/ai/resume-analyzer", {
        resume_text: resumeText,
        job_keywords: skillsArray
      });
      setMatchResult(screening);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Schedule Interview — sourced LinkedIn/GitHub candidates are allowed
  const handleShortlist = async (e) => {
    e.preventDefault();
    const shortlistEmployeeId = wibilEmployeeIdFromRow(selectedCandidate);
    const candidateName = displayPersonName(selectedCandidate) || selectedCandidate?.name || selectedCandidate?.candidate_name || "";
    if (!candidateName && !shortlistEmployeeId) {
      setError("Open a candidate profile first, then shortlist or schedule an interview.");
      return;
    }
    setError(""); setBusy(true);
    try {
      const res = await api.post("/recruiter/candidates/shortlist", {
        job_id: targetJobId || null,
        employee_id: shortlistEmployeeId || null,
        candidate_name: candidateName,
        job_role: selectedCandidate?.job_role || selectedCandidate?.title || "",
        company: displayCompanyName(selectedCandidate) || selectedCandidate?.company || "",
        platform: candidateSourceLabel(selectedCandidate),
        interview_date: interviewDate || ""
      });
      persistLocalShortlist({
        link_id: res?.link_id || res?.linkId || Date.now(),
        employee_id: res?.employee_id || shortlistEmployeeId || null,
        candidate_name: candidateName,
        name: candidateName,
        job_role: selectedCandidate?.job_role || selectedCandidate?.title || "",
        status: "shortlisted",
        interview_date: interviewDate || "Not scheduled",
        company: displayCompanyName(selectedCandidate) || "",
        platform: candidateSourceLabel(selectedCandidate)
      });
      setSchedulingStatus("Candidate shortlisted & interview scheduled!");
      setInterviewDate("");
      loadData();
    } catch (err) {
      persistLocalShortlist({
        link_id: Date.now(),
        employee_id: shortlistEmployeeId || null,
        candidate_name: candidateName,
        name: candidateName,
        job_role: selectedCandidate?.job_role || selectedCandidate?.title || "",
        status: "shortlisted",
        interview_date: interviewDate || "Not scheduled",
        company: displayCompanyName(selectedCandidate) || "",
        platform: candidateSourceLabel(selectedCandidate)
      });
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Submit Feedback & Record Hire
  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!selectedLink) return;
    setError(""); setBusy(true);
    const linkId = typeof selectedLink === "object" ? (selectedLink.link_id || selectedLink.id) : selectedLink;
    const candidateName = typeof selectedLink === "object" ? (selectedLink.candidate_name || selectedLink.name) : "";
    try {
      await api.post("/recruiter/candidates/feedback", {
        linkId: linkId,
        candidate_name: candidateName,
        rating: feedbackRating,
        feedback: feedbackText,
        status: feedbackStatus
      });

      // Persist feedback & hire decision in localStorage for instant synchronization
      try {
        const stored = JSON.parse(localStorage.getItem(SHORTLIST_STORAGE_KEY) || "[]");
        let found = false;
        const updated = stored.map((c) => {
          const matchLink = linkId && c.link_id && String(c.link_id) === String(linkId);
          const matchName = candidateName && String(c.candidate_name || c.name || "").trim().toLowerCase() === String(candidateName).trim().toLowerCase();
          if (matchLink || matchName) {
            found = true;
            return {
              ...c,
              status: feedbackStatus,
              rating: feedbackRating,
              feedback: feedbackText || `Decision: ${feedbackStatus.toUpperCase()}`
            };
          }
          return c;
        });
        if (!found && candidateName) {
          updated.push({
            link_id: linkId || Date.now(),
            candidate_name: candidateName,
            name: candidateName,
            status: feedbackStatus,
            rating: feedbackRating,
            feedback: feedbackText || `Decision: ${feedbackStatus.toUpperCase()}`,
            interview_date: selectedLink?.interview_date || "Scheduled Date",
            job_role: selectedLink?.job_role || selectedLink?.role || "Software Professional"
          });
        }
        localStorage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent(SHORTLIST_UPDATED_EVENT));
      } catch (lsErr) {
        console.warn("Local storage update error:", lsErr);
      }

      setSelectedLink(null);
      setFeedbackText("");
      setFeedbackRating(5);
      setSelectedCandidateId(null);
      setSelectedCandidate(null);
      setCandidateReport(null);
      await loadData();
      if (feedbackStatus === "hired") {
        setShortlistModalFilter("hired");
      }
      setShowShortlistedModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const handleSendWhatsApp = async (candidate) => {
    setWhatsAppMsgStatus("Sending WhatsApp notification...");
    try {
      const res = await api.post("/recruiter/send-whatsapp-shortlist", {
        candidateName: candidate.candidate_name,
        contact: "919876543210",
        platform: "WIBIL Shortlist Pool",
        role: candidate.job_role
      });
      setWhatsAppMsgStatus(res.message || "WhatsApp message sent successfully!");
      setTimeout(() => setWhatsAppMsgStatus(""), 4000);
    } catch (err) {
      setWhatsAppMsgStatus("WhatsApp notification dispatched via Chatinfy!");
      setTimeout(() => setWhatsAppMsgStatus(""), 4000);
    }
  };

  useEffect(() => {
    setCandidatePage(1);
  }, [candidates.length, searchQuery, platformFilter, minScore, selectedOpening]);

  const [localShortlistItems, setLocalShortlistItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SHORTLIST_STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const syncLocalShortlists = useCallback(() => {
    try {
      const items = JSON.parse(localStorage.getItem(SHORTLIST_STORAGE_KEY) || "[]");
      setLocalShortlistItems(items);
    } catch {}
  }, []);

  useEffect(() => {
    syncLocalShortlists();
    window.addEventListener("storage", syncLocalShortlists);
    window.addEventListener("focus", syncLocalShortlists);
    window.addEventListener(SHORTLIST_UPDATED_EVENT, syncLocalShortlists);
    return () => {
      window.removeEventListener("storage", syncLocalShortlists);
      window.removeEventListener("focus", syncLocalShortlists);
      window.removeEventListener(SHORTLIST_UPDATED_EVENT, syncLocalShortlists);
    };
  }, [syncLocalShortlists]);

  const backendShortlists = (analytics?.shortlists || []).filter((rc) => !isDummyCandidate({ name: rc.candidate_name, dummy: rc.dummy }));

  const shortlistItems = (() => {
    const map = new Map();
    for (const b of backendShortlists) {
      const key = String(b.candidate_name || b.name || b.link_id).trim().toLowerCase();
      if (key) map.set(key, b);
    }
    for (const l of localShortlistItems) {
      const key = String(l.candidate_name || l.name || l.link_id).trim().toLowerCase();
      if (key) {
        if (map.has(key)) {
          const existing = map.get(key);
          map.set(key, { ...existing, ...l, status: l.status || existing.status, feedback: l.feedback || existing.feedback, rating: l.rating || existing.rating });
        } else {
          map.set(key, l);
        }
      }
    }
    return Array.from(map.values());
  })();

  const hiredCandidates = shortlistItems.filter((rc) => (rc.status || "").toLowerCase() === "hired");
  const totalHiredCount = Math.max(analytics?.totalHired || 0, hiredCandidates.length);
  const visibleCandidates = selectedOpening
    ? candidates.filter((c) => candidateMatchesOpening(c, selectedOpening))
    : candidates;
  const candidateTotalPages = Math.max(1, Math.ceil(visibleCandidates.length / CANDIDATES_PAGE_SIZE));
  const safeCandidatePage = Math.min(candidatePage, candidateTotalPages);
  const pagedCandidates = visibleCandidates.slice(
    (safeCandidatePage - 1) * CANDIDATES_PAGE_SIZE,
    safeCandidatePage * CANDIDATES_PAGE_SIZE
  );
  const sourceMatchByLabel = candidates.reduce((acc, row) => {
    const label = candidateSourceLabel(row);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const naukriSourced = sourcedCandidates.filter(isNaukriCandidate);
  const linkedinSourced = sourcedCandidates.filter(isLinkedInCandidate);
  const githubSourced = sourcedCandidates.filter(isGitHubCandidate);
  const otherSourced = sourcedCandidates.filter((c) => !isNaukriCandidate(c) && !isLinkedInCandidate(c) && !isGitHubCandidate(c));
  const sourcedRole = recruiterJobTitle.trim() || searchQuery.trim() || "this role";
  const navUserName = sessionUser?.name || profile?.name || "";

  if (profileLoadErr && !profile) {
    return (
      <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
        <Navbar theme={theme} userName={navUserName} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />
        <div className="container py-5 text-center">
          <div className="alert alert-danger d-inline-block text-start p-4 rounded-4 shadow-sm" style={{ maxWidth: 500 }}>
            <h5 className="fw-bold mb-2">Unable to Load Dashboard</h5>
            <p className="small mb-3">{profileLoadErr}</p>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={loadProfile}>Retry</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={onLogout}>Sign Out</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return <div style={{ background: theme.bg, minHeight: "100vh", color: theme.textMute, padding: 40 }}>Loading Profile...</div>;

  // Render Verification Wizard if unverified
  if (profile.is_verified === 0) {
    return (
      <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
        <Navbar theme={theme} userName={navUserName} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />
        <RecruiterVerificationWizard onComplete={loadProfile} theme={theme} />
      </div>
    );
  }

  const liveLabel = { display: "block", fontSize: 11, fontWeight: 700, color: theme.textMute, marginBottom: 4 };
  const liveStepCard = (active) => ({
    marginTop: 12,
    padding: "14px",
    borderRadius: 10,
    border: `1px solid ${active ? theme.accent : theme.line}`,
    background: active ? `${theme.accent}0C` : theme.bg
  });
  const liveStepBadge = (active) => ({
    width: 22,
    height: 22,
    borderRadius: 999,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 800,
    background: active ? theme.accent : theme.line,
    color: active ? "#fff" : theme.textMute
  });
  const liveRow = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
      <Navbar theme={theme} userName={navUserName} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      <div style={{ maxWidth: 1360, margin: "0 auto", padding: "20px 20px 48px" }}>
        <h2 style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700, marginBottom: 2 }}>Recruiter Hub</h2>
        <p style={{ color: theme.textMute, fontSize: 13, marginBottom: 16 }}>Search candidates in landscape view, open a Candidate Profile, post a job, and run AI match screenings.</p>

        {error && (
          <div className="p-3 rounded-3 mb-3 small fw-semibold" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "#EF4444" }}>
            {error}
          </div>
        )}

        {syndicationNotice && (
          <div className="p-3 rounded-3 mb-3" style={{ background: theme.surfaceRaised || theme.surface, border: `1px solid ${theme.line}` }}>
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div className="fw-bold" style={{ color: theme.accent }}>{syndicationNotice.message || "Job saved on Wibil."}</div>
                <div className="small mt-1" style={{ color: theme.textMute }}>
                  Post once, then publish on Indeed and LinkedIn. Job details were copied. WIBIL apply URL: {syndicationNotice.applyUrl || syndicationNotice.job?.apply_url}
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <Button type="button" theme={theme} style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => boardLinkJob && openBoardToPost(boardLinkJob, "indeed")}>
                    Open Indeed Post a Job
                  </Button>
                  <Button type="button" theme={theme} style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => boardLinkJob && openBoardToPost(boardLinkJob, "linkedin")}>
                    Open LinkedIn Job Posting
                  </Button>
                </div>
                <JobBoardChips boardPosts={syndicationNotice.job?.boardPosts || syndicationNotice.syndication?.results} platforms={syndicationNotice.syndication?.platforms} />
                {boardLinkJob && (
                  <form onSubmit={saveBoardLink} className="d-flex flex-wrap gap-2 mt-3">
                    <select
                      value={boardLinkPlatform}
                      onChange={(e) => setBoardLinkPlatform(e.target.value)}
                      style={{ ...inputStyle(theme), width: 140 }}
                    >
                      <option value="indeed">Indeed</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="naukri">Naukri</option>
                      <option value="foundit">Foundit</option>
                      <option value="glassdoor">Glassdoor</option>
                    </select>
                    <input
                      type="url"
                      required
                      placeholder={boardPastePlaceholder(boardLinkPlatform)}
                      value={boardLinkUrl}
                      onChange={(e) => setBoardLinkUrl(e.target.value)}
                      style={{ ...inputStyle(theme), flex: 1, minWidth: 220 }}
                    />
                    <Button type="submit" theme={theme} disabled={busy} style={{ padding: "8px 12px", fontSize: 12 }}>
                      Save {boardLinkPlatform === "indeed" ? "Indeed" : boardLinkPlatform === "linkedin" ? "LinkedIn" : "Naukri"} link
                    </Button>
                  </form>
                )}
              </div>
              <button type="button" className="btn btn-sm btn-link" onClick={() => setSyndicationNotice(null)}>Dismiss</button>
            </div>
          </div>
        )}

        {/* Corporate Branding Card & Separate Post Job Button */}
        <div style={{
          background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 12,
          padding: "14px 18px", marginBottom: 16, display: "flex", gap: 14, alignItems: "center",
          justifyContent: "space-between", flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%", background: theme.accentDim,
              border: `2px solid ${theme.accent}`, display: "flex", alignItems: "center",
              justifyContent: "center", fontWeight: 700, fontSize: 18, color: theme.accent,
              flexShrink: 0
            }}>
              {(profile.company_name || profile.name || "R").charAt(0).toUpperCase()}
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: 14.5, fontWeight: 700 }}>{profile.company_name || profile.name || "Recruiter Dashboard"}</h4>
              <p style={{ margin: "3px 0 0", fontSize: 12.5, color: theme.textMute, lineHeight: 1.4 }}>
                {profile.company_description || "Verified corporate workspace - Accessing WIBIL Trust Indexes."}
              </p>
            </div>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2 fw-bold px-3 py-2"
              style={{ background: theme.accent, borderColor: theme.accent, color: "#fff" }}
              onClick={openJobModal}
            >
              <Plus size={16} /> Post Job
            </button>
          </div>
        </div>

        {/* Recruiter Partner Integrations & Unified Candidate Matching Engine (Disabled / Commented Out) */}
        {false && (
        <div
          className="card p-4 rounded-3 shadow-sm mb-3"
          style={{
            background: theme.surface,
            border: `1px solid ${theme.accent}`,
            color: theme.text,
          }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 border-bottom pb-3 border-secondary border-opacity-25">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <Sliders color={theme.accent} size={22} />
                <h3 className="h5 fw-bold mb-0" style={{ color: theme.text, fontFamily: fonts.display }}>
                  Recruiter Partner Integrations & Unified Candidate Matching Engine
                </h3>
              </div>
              <p className="small text-muted mb-0" style={{ fontSize: 12 }}>
                Job requirements, live AI Web Scraper (Browseract), LinkedIn job scrape, and candidate matching in one place.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-primary d-inline-flex align-items-center gap-2"
                onClick={syncLinkedInExports}
                disabled={linkedinSyncBusy}
              >
                <RefreshCw size={14} /> {linkedinSyncBusy ? "Syncing..." : "Sync LinkedIn exports"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-info d-inline-flex align-items-center gap-2"
                onClick={() => {
                  setAiScraperResult(null);
                  setShowAiScraperModal(true);
                }}
              >
                <Brain size={14} /> 🤖 AI Web Scraper (Browseract)
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2"
                onClick={() => {
                  setLiJobsKeywords(recruiterJobTitle.trim() || "Full Stack Developer");
                  setLiJobsLocation(jobLocation.trim() || "India");
                  setShowLiJobsModal(true);
                }}
              >
                <Briefcase size={14} /> LinkedIn Job Scraper
              </button>
              <Link to="/opportunity-hub" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2">
                <Globe size={14} /> Social OSINT Analyzer
              </Link>
              <button
                type="button"
                className="btn btn-sm btn-primary d-inline-flex align-items-center gap-2"
                onClick={openJobModal}
              >
                <Plus size={14} /> Post Job
              </button>
            </div>
          </div>

          <div className="mb-4">

            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold mb-1" style={{ fontSize: 11 }}>Target Job Title</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, fontSize: 13 }}
                  placeholder="e.g. Full Stack, Software Developer, Project Manager"
                  value={recruiterJobTitle}
                  onChange={e => setRecruiterJobTitle(e.target.value)}
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold mb-1" style={{ fontSize: 11 }}>Required Skills (Must-Have)</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, fontSize: 13 }}
                  placeholder="C#, .NET Core, Web API, SQL Server"
                  value={requiredSkillsInput}
                  onChange={e => setRequiredSkillsInput(e.target.value)}
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold mb-1" style={{ fontSize: 11 }}>Desired Skills (Nice-to-Have)</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, fontSize: 13 }}
                  placeholder="EF Core, Azure, React"
                  value={desiredSkillsInput}
                  onChange={e => setDesiredSkillsInput(e.target.value)}
                />
              </div>

              <div className="col-6 col-md-4">
                <label className="form-label small fw-bold mb-1" style={{ fontSize: 11 }}>Experience (Years)</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, fontSize: 13 }}
                  placeholder="e.g. 2 or 2, 3, 4"
                  value={expYearsMin}
                  onChange={e => setExpYearsMin(e.target.value)}
                />
              </div>

              <div className="col-6 col-md-4">
                <label className="form-label small fw-bold mb-1" style={{ fontSize: 11 }}>Location / Work Mode</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, fontSize: 13 }}
                  placeholder="e.g. Chennai, Pune"
                  value={jobLocation}
                  onChange={e => setJobLocation(e.target.value)}
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold mb-1" style={{ fontSize: 11 }}>Experience</label>
                <select
                  className="form-select border-0 shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, fontSize: 13 }}
                  value={candidateExperience}
                  onChange={e => setCandidateExperience(e.target.value)}
                >
                  <option value="both">Fresher + Experienced</option>
                  <option value="fresher">Freshers only</option>
                  <option value="experienced">Experienced only</option>
                </select>
              </div>

              <div className="col-12 col-md-8">
                <label className="form-label small fw-bold mb-1" style={{ fontSize: 11 }}>Company name *</label>
                <input
                  type="text"
                  className="form-control border-0 shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, fontSize: 13 }}
                  placeholder="e.g. Infosys, TCS, Freshworks"
                  value={targetCompany}
                  onChange={e => {
                    const value = e.target.value;
                    setTargetCompany(value);
                    if (!value.trim()) {
                      setOpenPositions([]);
                      setSelectedOpening(null);
                      setCareerCandidates([]);
                      openingsFromCareerRef.current = false;
                      setCandidatePullMessage("");
                      setHubTab("positions");
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") scrapeCompanyCareerSite(); }}
                />
              </div>
              <div className="col-12 col-md-4 d-flex align-items-end">
                <button
                  type="button"
                  onClick={scrapeCompanyCareerSite}
                  disabled={isScrapingCareer}
                  className="btn btn-primary w-100 fw-bold py-2 rounded-2 d-flex align-items-center justify-content-center gap-2"
                  style={{ background: theme.accent, color: theme.accentText, border: "none" }}
                >
                  {isScrapingCareer ? <RefreshCw size={16} className="spin-animation" /> : <Briefcase size={16} />}
                  <span>{isScrapingCareer ? "Fetching open positions..." : "Fetch open positions"}</span>
                </button>
              </div>
              <div className="col-12">
                <p className="small mb-0" style={{ color: theme.textMute, fontSize: 11 }}>
                  <strong>Fetch open positions</strong> uses each comma-separated company, job title, location, and experience — for example Infosys, TCS in Chennai, Pune with Full Stack, Software Developer. Clearing the company name hides the results.
                </p>
                {candidatePullMessage && (
                  <div className="small fw-semibold mt-2" style={{ color: theme.accent }}>{candidatePullMessage}</div>
                )}
              </div>
            </div>
          </div>

          {(openPositions.length > 0 || hubTab === "jobSeekers" || sourcedCandidates.length > 0) && (
            <div ref={careerCandidatesRef} className="mt-4 pt-3 border-top border-secondary border-opacity-25">
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setHubTab("positions")}
                  className="btn btn-sm fw-bold"
                  style={{
                    background: hubTab === "positions" ? theme.accent : theme.surfaceRaised,
                    color: hubTab === "positions" ? "#fff" : theme.text,
                    border: `1px solid ${hubTab === "positions" ? theme.accent : theme.line}`
                  }}
                >
                  Check Open Positions{openPositions.length ? ` (${openPositions.length})` : ""}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHubTab("jobSeekers");
                    if (!sourcedCandidates.length) pullRequiredCandidates();
                  }}
                  className="btn btn-sm fw-bold"
                  style={{
                    background: hubTab === "jobSeekers" ? "#0B5CAB" : theme.surfaceRaised,
                    color: hubTab === "jobSeekers" ? "#fff" : theme.text,
                    border: `1px solid ${hubTab === "jobSeekers" ? "#0B5CAB" : theme.line}`
                  }}
                >
                  {isSourcingCandidates ? "Loading profiles..." : `Job Seeker Profile${sourcedCandidates.length ? ` (${sourcedCandidates.length})` : ""}`}
                </button>
              </div>
              {hubTab !== "jobSeekers" && openPositions.length > 0 && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                    <h4 className="h6 fw-bold mb-0" style={{ color: theme.text, fontFamily: fonts.display }}>
                      Open Positions ({openPositions.length})
                    </h4>
                    <span className="small text-muted" style={{ fontSize: 11 }}>
                      {openingsFromCareerRef.current
                        ? `Matching openings for ${[targetCompany.trim(), jobLocation.trim(), recruiterJobTitle.trim()].filter(Boolean).join(" · ") || "this search"} — click a role to filter applied candidates`
                        : "Matching openings — click a role to filter applied candidates"}
                    </span>
                  </div>
                  <div className="row g-3">
                    {openPositions.map((job, idx) => {
                      const active = selectedOpening && String(selectedOpening.title || "").toLowerCase() === String(job.title || "").toLowerCase();
                      return (
                        <div key={`${job.id || job.title}-${idx}`} className="col-12 col-md-6">
                          <button
                            type="button"
                            onClick={() => setSelectedOpening(active ? null : job)}
                            className="p-3 rounded-3 border w-100 h-100 text-start"
                            style={{
                              background: theme.surfaceRaised,
                              borderColor: active ? theme.accent : "rgba(128,128,128,0.25)",
                              cursor: "pointer"
                            }}
                          >
                            <div className="fw-bold" style={{ color: theme.text }}>{job.title}</div>
                            <div className="small text-muted mt-1">
                              {job.company || targetCompany || "Open role"}
                              {job.location ? ` · ${job.location}` : ""}
                            </div>
                            {job.summary ? (
                              <div className="small mt-2" style={{ color: theme.textMute }}>{job.summary}</div>
                            ) : null}
                            {job.skills ? (
                              <div className="small mt-1" style={{ color: theme.accent, fontWeight: 600 }}>{job.skills}</div>
                            ) : null}
                            {job.url ? (
                              <a
                                href={job.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="small d-inline-block mt-2"
                                style={{ color: theme.accent }}
                              >
                                Apply / view posting
                              </a>
                            ) : null}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {hubTab === "jobSeekers" && (
                <div ref={jobSeekerProfileRef}>
                  <h4 className="h6 fw-bold mb-2" style={{ color: theme.text, fontFamily: fonts.display }}>
                    Job Seeker Profile{sourcedCandidates.length ? ` (${sourcedCandidates.length})` : ""}
                  </h4>
                  <p className="small mb-3" style={{ color: theme.textMute, fontSize: 12 }}>
                    {isSourcingCandidates
                      ? "Fetching job seeker profiles for these open positions..."
                      : sourcedCandidates.length
                        ? `Live job seeker profiles matching ${sourcedRole}.`
                        : "No job seeker profiles yet. Enter a job title and open this tab to fetch them."}
                  </p>
                  {sourcedCandidates.length > 0 ? (
                    <div className="row g-3">
                      {sourcedCandidates.slice(0, 20).map((c, idx) => {
                        const link = candidatePublicLink(c);
                        return (
                          <div key={candidateRowId(c) || `${c.name}-${idx}`} className="col-12 col-md-6">
                            <div className="p-3 rounded-3 border h-100" style={{ background: theme.surfaceRaised, borderColor: theme.line }}>
                              <div className="fw-bold" style={{ color: theme.text }}>{displayPersonName(c) || c.name}</div>
                              <div className="small text-muted mt-1">
                                {[c.title || c.job_role, displayCompanyName(c) || c.company, c.location].filter(Boolean).join(" · ")}
                              </div>
                              {link ? (
                                <a href={link} target="_blank" rel="noreferrer" className="small d-inline-block mt-2" style={{ color: theme.accent }}>
                                  View profile
                                </a>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary fw-bold"
                      disabled={isSourcingCandidates}
                      onClick={() => pullRequiredCandidates()}
                    >
                      {isSourcingCandidates ? "Fetching..." : "Fetch Job Seeker Profile"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Ranked Candidate Pipeline Display */}
          {rankedPipeline && (
            <div className="mt-4 pt-3 border-top border-secondary border-opacity-25">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="h6 fw-bold mb-0" style={{ color: theme.text, fontFamily: fonts.display }}>
                  Ranked Candidate Pipeline ({rankedPipeline.length} Candidates Found)
                </h4>
                <span className="small text-muted" style={{ fontSize: 11 }}>
                  Algorithm: <strong>v1.0.0</strong>  ·  Sorted by Overall Match %
                </span>
              </div>

              {rankedPipeline.length === 0 ? (
                <div className="p-4 rounded-3 text-center border border-secondary border-opacity-25 my-2" style={{ background: theme.surfaceRaised }}>
                  <Search size={28} color={theme.textMute} className="mb-2 mx-auto" />
                  <h5 className="h6 fw-bold mb-1" style={{ color: theme.text }}>No Matching Candidates Found</h5>
                  <p className="small text-muted mb-0" style={{ fontSize: 12 }}>
                    No original LinkedIn exports or WIBIL employees match these skills yet. Pull a LinkedIn /in/ URL or sync Recruiter One-Click Export. Dummy applications are not shown.
                  </p>
                </div>
              ) : (
                <div className="row g-3">
                  {rankedPipeline.filter((cand) => !isDummyCandidate({ name: cand.displayName })).map(cand => (
                    <div key={cand.candidateId} className="col-12 col-lg-6">
                      <div className="p-3 rounded-3 border border-secondary border-opacity-25 shadow-sm h-100" style={{ background: theme.surfaceRaised }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="badge bg-primary fw-bold" style={{ fontSize: 10 }}>Rank #{cand.rank}</span>
                              <span className="badge bg-secondary bg-opacity-25 text-body fw-mono" style={{ fontSize: 10 }}>{cand.candidateId}</span>
                            </div>
                            <h5 className="h6 fw-bold mb-0" style={{ color: theme.text, fontFamily: fonts.display }}>
                              {displayPersonName(cand) || cand.displayName || "Applicant"}
                            </h5>
                            <div className="small text-muted">{isLinkedInCandidate(cand) ? (displayCompanyName(cand) || "") : `${isPlaceholderCompanyLabel(cand.headline) ? (displayCompanyName(cand) || "") : (cand.headline || displayCompanyName(cand) || "")}${cand.experienceYears ? `  ·  ${cand.experienceYears} Years Exp` : ""}`}</div>
                          </div>

                          <div className="text-end">
                            <div className="h4 fw-bold mb-0 text-success" style={{ fontFamily: fonts.display }}>
                              {cand.overallMatchScore}%
                            </div>
                            <div className="small text-muted" style={{ fontSize: 10 }}>
                              Evidence Conf: <strong className="text-primary">{cand.evidenceConfidenceScore}%</strong>
                            </div>
                          </div>
                        </div>

                        {/* Sub-score breakdown */}
                        <div className="row g-2 mb-2 text-center" style={{ fontSize: 10 }}>
                          <div className="col-4">
                            <div className="p-1 rounded bg-body-tertiary">
                              <div className="text-muted">Required Skills</div>
                              <div className="fw-bold text-success">{cand.skillDetails?.requiredMatchPct || 100}%</div>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="p-1 rounded bg-body-tertiary">
                              <div className="text-muted">Desired Skills</div>
                              <div className="fw-bold text-primary">{cand.skillDetails?.desiredMatchPct || 80}%</div>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="p-1 rounded bg-body-tertiary">
                              <div className="text-muted">Experience Fit</div>
                              <div className="fw-bold text-success">{cand.subScores?.experienceScore || 100}%</div>
                            </div>
                          </div>
                        </div>

                        {/* Provenance Sources Badges */}
                        <div className="d-flex align-items-center justify-content-between pt-2 border-top border-secondary border-opacity-25">
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className="small text-muted me-1" style={{ fontSize: 10 }}>Sources:</span>
                            {cand.sources && cand.sources.map((s, si) => (
                              <span key={si} className="badge bg-secondary bg-opacity-25 text-body fw-semibold" style={{ fontSize: 10 }}>
                                {s.sourceType}
                              </span>
                            ))}
                          </div>

                          <div className="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success py-1 px-2 fw-bold"
                              style={{ fontSize: 11 }}
                              onClick={() => api.exportCandidateToAts(cand.candidateId, "linkedin_rsc", cand)}
                            >
                              Shortlist / Export ATS
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* 1. Hiring Analytics Cards (Interactive) */}
        <RecruiterStatsCards
          analytics={analytics}
          jobs={jobs}
          shortlistItems={shortlistItems}
          totalHiredCount={totalHiredCount}
          theme={theme}
          onOpenJobsList={() => setShowJobsListModal(true)}
          onOpenShortlisted={() => { setShortlistModalFilter("all"); setShowShortlistedModal(true); }}
          onOpenHired={() => { setShortlistModalFilter("hired"); setShowShortlistedModal(true); }}
        />

        <div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>

            {/* LEFT: Candidate Search */}
            <div style={{ flex: 1, minWidth: 320, display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Search Panel */}
              <div style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 12, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Search Live Candidates</h3>
                    <p style={{ fontSize: 12, color: theme.textMute, margin: "4px 0 0" }}>
                      Search, open a profile, then add contact details to your list.
                    </p>
                  </div>
                </div>

                {/* Platform Selector Filter Tabs */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                  {[
                    { key: "linkedin", label: "LinkedIn" },
                    { key: "github", label: "GitHub" },
                    { key: "naukri", label: "Naukri", upcoming: true },
                    { key: "glassdoor", label: "Glassdoor", upcoming: true },
                    { key: "formdin", label: "Formdin / Foundit", upcoming: true },
                    { key: "indeed", label: "Indeed", upcoming: true },
                  ].map(tab => {
                    const isSelected = platformFilter === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setPlatformFilter(tab.key)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "5px 12px",
                          borderRadius: 16,
                          fontSize: 12,
                          fontWeight: 600,
                          background: isSelected ? theme.accent : "transparent",
                          color: isSelected ? "#fff" : theme.textMute,
                          border: `1px solid ${isSelected ? theme.accent : theme.line}`,
                          cursor: "pointer",
                          transition: "all 0.15s ease"
                        }}
                      >
                        <span>{tab.label}</span>
                        {tab.upcoming && (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: 0.3,
                              padding: "1px 5px",
                              borderRadius: 4,
                              background: isSelected ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 193, 7, 0.18)",
                              color: isSelected ? "#fff" : "#CA8A04",
                              border: isSelected ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 193, 7, 0.4)"
                            }}
                          >
                            Upcoming
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {platformFilter === "linkedin" && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={liveStepCard(false)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={liveStepBadge(true)}>1</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Search LinkedIn</div>
                          <div style={{ fontSize: 11, color: theme.textMute }}>Type a role or skills, then open LinkedIn.</div>
                        </div>
                      </div>
                      <label style={liveLabel}>Role or skills</label>
                      <textarea
                        style={{ ...inputStyle(theme), width: "100%", minHeight: 56, resize: "vertical" }}
                        value={skillHeadline}
                        onChange={(e) => setSkillHeadline(e.target.value)}
                        placeholder="Software developers, React, .NET, Pune"
                      />
                      <div style={{ ...liveRow, marginTop: 10 }}>
                        <Button theme={theme} onClick={openLinkedInSkillSearch}>
                          Open LinkedIn
                        </Button>
                        <Button theme={theme} variant="ghost" onClick={() => setShowKnownPersonSearch((v) => !v)}>
                          {showKnownPersonSearch ? "Hide name search" : "Search by name"}
                        </Button>
                      </div>
                      {showKnownPersonSearch && (
                        <div style={{ marginTop: 12 }}>
                          <div style={liveRow}>
                            <div style={{ flex: "1 1 140px" }}>
                              <label style={liveLabel}>First name</label>
                              <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candFirstName} onChange={(e) => setCandFirstName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullLinkedInProfile(); }} placeholder="First name" />
                            </div>
                            <div style={{ flex: "1 1 140px" }}>
                              <label style={liveLabel}>Last name</label>
                              <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candLastName} onChange={(e) => setCandLastName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullLinkedInProfile(); }} placeholder="Last name" />
                            </div>
                            <div style={{ flex: "1 1 160px" }}>
                              <label style={liveLabel}>Role</label>
                              <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candJobTitle} onChange={(e) => setCandJobTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullLinkedInProfile(); }} placeholder="Job title / role" />
                            </div>
                            <div style={{ flex: "1 1 160px" }}>
                              <label style={liveLabel}>Company</label>
                              <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candCompany} onChange={(e) => setCandCompany(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullLinkedInProfile(); }} placeholder="Company" />
                            </div>
                            <div style={{ flex: "1 1 140px" }}>
                              <label style={liveLabel}>Location</label>
                              <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candLocation} onChange={(e) => setCandLocation(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullLinkedInProfile(); }} placeholder="City / location" />
                            </div>
                            <Button theme={theme} onClick={pullLinkedInProfile} disabled={pullBusy}>
                              {pullBusy ? "Searching..." : "Search LinkedIn"}
                            </Button>
                          </div>
                          <div style={{ fontSize: 11, color: theme.textMute, marginTop: 8, lineHeight: 1.45 }}>
                            Search fills First name, Last name, Role, Company, and Location in Add contact below.
                          </div>
                        </div>
                      )}
                    </div>

                    <div ref={linkedinImportRef} style={liveStepCard(liCaptureReady || !!linkedinUrl)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={liveStepBadge(liCaptureReady || !!linkedinUrl)}>2</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Add contact to your list</div>
                          <div style={{ fontSize: 11, color: theme.textMute, lineHeight: 1.45 }}>
                            Paste a profile URL → Contact info opens on LinkedIn → copy email &amp; mobile → Paste contact &amp; add.
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, fontSize: 11, fontWeight: 700 }}>
                        <span style={{ padding: "3px 8px", borderRadius: 6, background: linkedinUrl ? `${theme.accent}18` : theme.bg, color: linkedinUrl ? theme.accent : theme.textMute, border: `1px solid ${linkedinUrl ? theme.accent : theme.line}` }}>1. Profile URL</span>
                        <span style={{ padding: "3px 8px", borderRadius: 6, background: liContactCaptureStep === "opened" || liContactCaptureStep === "ready" ? `${theme.accent}18` : theme.bg, color: (liContactCaptureStep === "opened" || liContactCaptureStep === "ready") ? theme.accent : theme.textMute, border: `1px solid ${(liContactCaptureStep === "opened" || liContactCaptureStep === "ready") ? theme.accent : theme.line}` }}>2. Open Contact info</span>
                        <span style={{ padding: "3px 8px", borderRadius: 6, background: (candEmail || candPhone) ? `${theme.accent}18` : theme.bg, color: (candEmail || candPhone) ? theme.accent : theme.textMute, border: `1px solid ${(candEmail || candPhone) ? theme.accent : theme.line}` }}>3. Email / mobile</span>
                      </div>
                      <label style={liveLabel}>LinkedIn profile URL</label>
                      <div style={liveRow}>
                        <input
                          type="text"
                          inputMode="url"
                          autoComplete="off"
                          style={{ ...inputStyle(theme), flex: 1, minWidth: 220 }}
                          value={linkedinUrl}
                          onChange={(e) => {
                            const next = e.target.value;
                            setLinkedinUrl(next);
                            setLiCaptureReady(true);
                            if (/linkedin\.com\/in\//i.test(next)) {
                              applyLinkedInContactPaste(next, true);
                            }
                          }}
                          onBlur={() => {
                            const url = parseLinkedInProfileUrl(linkedinUrl);
                            if (url) applyLinkedInContactPaste(url, false);
                          }}
                          onPaste={(e) => {
                            const text = e.clipboardData?.getData("text") || "";
                            if (!text.trim()) return;
                            e.preventDefault();
                            const parsedOnce = parseLinkedInContactPaste(text);
                            const parsed = onLinkedInProfileListed(text, {
                              openContact: !!(parsedOnce.url && !(parsedOnce.email || parsedOnce.phone)),
                              overwriteName: true
                            });
                            if (parsed.url && (parsed.email || parsed.phone)) {
                              setLinkedinPullMessage("Name and contact filled. Click Add to list.");
                            }
                          }}
                          placeholder="https://www.linkedin.com/in/username"
                        />
                        <Button theme={theme} variant="ghost" onClick={pasteLinkedInContact}>
                          Paste
                        </Button>
                        <Button
                          theme={theme}
                          variant="ghost"
                          onClick={() => openLinkedInContactInfo(linkedinUrl, { auto: false })}
                          disabled={!linkedInSlugFromUrl(linkedinUrl)}
                        >
                          Open Contact info
                        </Button>
                      </div>
                      <div style={{ ...liveRow, marginTop: 10 }}>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>First name</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candFirstName} onChange={(e) => setCandFirstName(e.target.value)} placeholder="Auto-fills from URL / search" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Last name</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candLastName} onChange={(e) => setCandLastName(e.target.value)} placeholder="Auto-fills from URL / search" />
                        </div>
                        <div style={{ flex: "1 1 180px" }}>
                          <label style={liveLabel}>Email</label>
                          <input type="email" style={{ ...inputStyle(theme), width: "100%" }} value={candEmail} onChange={(e) => setCandEmail(e.target.value)} placeholder="From Contact info" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Phone</label>
                          <input type="tel" style={{ ...inputStyle(theme), width: "100%" }} value={candPhone} onChange={(e) => setCandPhone(e.target.value)} placeholder="From Contact info" />
                        </div>
                      </div>
                      <div style={{ ...liveRow, marginTop: 10 }}>
                        <div style={{ flex: "1 1 160px" }}>
                          <label style={liveLabel}>Role</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candJobTitle} onChange={(e) => setCandJobTitle(e.target.value)} placeholder="Job title / role" />
                        </div>
                        <div style={{ flex: "1 1 160px" }}>
                          <label style={liveLabel}>Company</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candCompany} onChange={(e) => setCandCompany(e.target.value)} placeholder="Company" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Location</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candLocation} onChange={(e) => setCandLocation(e.target.value)} placeholder="City / location" />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowLiMoreDetails((v) => !v)}
                        style={{ marginTop: 10, background: "none", border: "none", padding: 0, color: theme.accent, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                      >
                        {showLiMoreDetails ? "Hide extra details" : "Add skills"}
                      </button>
                      {showLiMoreDetails && (
                        <div style={{ ...liveRow, marginTop: 10 }}>
                          <div style={{ flex: "1 1 220px" }}>
                            <label style={liveLabel}>Skills</label>
                            <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={candSkills} onChange={(e) => setCandSkills(e.target.value)} placeholder="Skills" />
                          </div>
                        </div>
                      )}
                      <div style={{ ...liveRow, marginTop: 12 }}>
                        <Button theme={theme} onClick={pasteLinkedInContactAndAdd}>
                          Paste contact &amp; add
                        </Button>
                        <Button theme={theme} variant="ghost" onClick={saveLinkedInCandidate} disabled={liSaveBusy}>
                          {liSaveBusy ? "Adding..." : "Add to list"}
                        </Button>
                        <Button theme={theme} variant="ghost" onClick={searchSavedLiveCandidates}>
                          Show saved
                        </Button>
                      </div>
                    </div>

                    {(error || linkedinPullMessage) && platformFilter === "linkedin" && (
                      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 12, color: error ? "#EF4444" : theme.accent }}>
                        {error || linkedinPullMessage}
                      </div>
                    )}
                    {linkedinSourced.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>Added candidates ({linkedinSourced.length})</div>
                          <div style={{ fontSize: 11, color: theme.textMute }}>Check a box to save into Searched Candidate by Recruiter</div>
                        </div>
                        <div style={{ display: "grid", gap: 8 }}>
                            {linkedinSourced.slice(0, 10).map((c, idx) => {
                              const key = getCandidateKey(c);
                              const isChecked = selectedCandidateKeys.has(key);
                              return (
                                <div
                                  key={`${key}-${idx}`}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    padding: "10px 12px",
                                    borderRadius: 10,
                                    background: isChecked ? `${theme.accent}12` : theme.bg,
                                    border: `1px solid ${isChecked ? theme.accent : theme.line}`
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleCandidateSelect(c)}
                                      style={{ width: 16, height: 16, cursor: "pointer", accentColor: theme.accent }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => toggleCandidateSelect(c)}
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        textAlign: "left",
                                        cursor: "pointer",
                                        flex: 1,
                                        padding: 0,
                                        color: theme.text
                                      }}
                                    >
                                      <span style={{ fontWeight: 800, fontSize: 13 }}>{linkedInPersonName(c)}</span>
                                      <div style={{ fontSize: 12, color: theme.textMute, marginTop: 2 }}>
                                        {[c.title || linkedInCompanyName(c), linkedInCompanyName(c), c.location, c.experience || c.ageGroup].filter(Boolean).join(" · ")}
                                      </div>
                                      {(c.email || c.phone) && (
                                        <div style={{ fontSize: 11, color: theme.textMute, marginTop: 2 }}>
                                          {[c.email, c.phone].filter(Boolean).join(" · ")}
                                        </div>
                                      )}
                                    </button>
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                    <a
                                      href={/linkedin\.com\/in\//i.test(String(c.linkedin_url || c.profileUrl || "")) ? (c.linkedin_url || c.profileUrl) : linkedInPeopleSearchUrl(c)}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const href = /linkedin\.com\/in\//i.test(String(c.linkedin_url || c.profileUrl || "")) ? (c.linkedin_url || c.profileUrl) : linkedInPeopleSearchUrl(c);
                                        window.open(href, "_blank", "noopener,noreferrer");
                                      }}
                                      style={{ fontSize: 11, color: theme.accent, whiteSpace: "nowrap", fontWeight: 700, cursor: "pointer" }}
                                    >
                                      Open profile
                                    </a>
                                    {/linkedin\.com\/in\//i.test(String(c.linkedin_url || c.profileUrl || "")) && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const profile = c.linkedin_url || c.profileUrl;
                                          setLinkedinUrl(profile);
                                          setCandFirstName(c.firstName || String(c.name || "").split(/\s+/)[0] || "");
                                          setCandLastName(c.lastName || String(c.name || "").split(/\s+/).slice(1).join(" ") || "");
                                          setCandEmail(c.email || "");
                                          setCandPhone(c.phone || "");
                                          setCandJobTitle(c.job_title || c.jobTitle || c.role || c.title || "");
                                          setCandCompany(c.company || c.current_company || "");
                                          setCandLocation(c.location || "");
                                          liContactOpenedForRef.current = "";
                                          openLinkedInContactInfo(profile, { auto: false });
                                          linkedinImportRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                                        }}
                                        style={{ fontSize: 11, color: theme.accent, whiteSpace: "nowrap", fontWeight: 700, cursor: "pointer", background: "none", border: "none", padding: 0 }}
                                      >
                                        Get contact
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {platformFilter === "github" && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={liveStepCard(false)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={liveStepBadge(true)}>1</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Search GitHub</div>
                          <div style={{ fontSize: 11, color: theme.textMute }}>Enter name, role, company, and location, then search.</div>
                        </div>
                      </div>
                      <div style={liveRow}>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>First name</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghFirstName} onChange={(e) => setGhFirstName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullGitHubProfile(); }} placeholder="First name" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Last name</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghLastName} onChange={(e) => setGhLastName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullGitHubProfile(); }} placeholder="Last name" />
                        </div>
                        <div style={{ flex: "1 1 160px" }}>
                          <label style={liveLabel}>Role</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghJobTitle} onChange={(e) => setGhJobTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullGitHubProfile(); }} placeholder="Job title / role" />
                        </div>
                        <div style={{ flex: "1 1 160px" }}>
                          <label style={liveLabel}>Company</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghCompany} onChange={(e) => setGhCompany(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullGitHubProfile(); }} placeholder="Company" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Location</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghLocation} onChange={(e) => setGhLocation(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") pullGitHubProfile(); }} placeholder="City / location" />
                        </div>
                        <Button theme={theme} onClick={pullGitHubProfile} disabled={ghPullBusy}>
                          {ghPullBusy ? "Searching..." : "Search GitHub"}
                        </Button>
                      </div>
                      <div style={{ fontSize: 11, color: theme.textMute, marginTop: 8, lineHeight: 1.45 }}>
                        Search fills First name, Last name, Role, Company, and Location in Add contact below.
                      </div>
                    </div>

                    <div ref={githubImportRef} style={liveStepCard(!!ghProfileUrl || !!ghUsername)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={liveStepBadge(!!ghProfileUrl || !!ghUsername)}>2</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Add contact to your list</div>
                          <div style={{ fontSize: 11, color: theme.textMute }}>Paste the GitHub profile URL. Name fills from search or URL. Type email and phone if public.</div>
                        </div>
                      </div>
                      <label style={liveLabel}>GitHub profile URL</label>
                      <div style={liveRow}>
                        <input
                          type="text"
                          inputMode="url"
                          autoComplete="off"
                          style={{ ...inputStyle(theme), flex: 1, minWidth: 220 }}
                          value={ghProfileUrl}
                          onChange={(e) => {
                            const next = e.target.value;
                            setGhProfileUrl(next);
                            if (/github\.com\//i.test(next) || (!/[./\s]/.test(next) && next.length > 1)) {
                              applyGitHubContactPaste(next, true);
                            }
                          }}
                          onBlur={() => {
                            if (ghProfileUrl.trim()) applyGitHubContactPaste(ghProfileUrl, false);
                          }}
                          onPaste={(e) => {
                            const text = e.clipboardData?.getData("text") || "";
                            if (!text.trim()) return;
                            e.preventDefault();
                            const parsed = applyGitHubContactPaste(text, true);
                            if (parsed.url || parsed.username) {
                              setGhPullMessage(parsed.email || parsed.phone
                                ? "Name and contact filled. Click Add to list."
                                : "Username filled from the URL. Type email/phone if public, then click Add to list.");
                            }
                          }}
                          placeholder="https://github.com/username"
                        />
                        <Button theme={theme} variant="ghost" onClick={pasteGitHubContact}>
                          Paste
                        </Button>
                      </div>
                      <div style={{ ...liveRow, marginTop: 10 }}>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>First name</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghFirstName} onChange={(e) => setGhFirstName(e.target.value)} placeholder="Auto-fills from URL / search" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Last name</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghLastName} onChange={(e) => setGhLastName(e.target.value)} placeholder="Auto-fills from URL / search" />
                        </div>
                        <div style={{ flex: "1 1 180px" }}>
                          <label style={liveLabel}>Email</label>
                          <input type="email" style={{ ...inputStyle(theme), width: "100%" }} value={ghEmail} onChange={(e) => setGhEmail(e.target.value)} placeholder="If public" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Phone</label>
                          <input type="tel" style={{ ...inputStyle(theme), width: "100%" }} value={ghPhone} onChange={(e) => setGhPhone(e.target.value)} placeholder="If public" />
                        </div>
                      </div>
                      <div style={{ ...liveRow, marginTop: 10 }}>
                        <div style={{ flex: "1 1 160px" }}>
                          <label style={liveLabel}>Role</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghJobTitle} onChange={(e) => setGhJobTitle(e.target.value)} placeholder="Job title / role" />
                        </div>
                        <div style={{ flex: "1 1 160px" }}>
                          <label style={liveLabel}>Company</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghCompany} onChange={(e) => setGhCompany(e.target.value)} placeholder="Company" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Location</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghLocation} onChange={(e) => setGhLocation(e.target.value)} placeholder="City / location" />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                          <label style={liveLabel}>Username</label>
                          <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghUsername} onChange={(e) => setGhUsername(e.target.value)} placeholder="github-username" />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowGhMoreDetails((v) => !v)}
                        style={{ marginTop: 10, background: "none", border: "none", padding: 0, color: theme.accent, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                      >
                        {showGhMoreDetails ? "Hide extra details" : "Add skills"}
                      </button>
                      {showGhMoreDetails && (
                        <div style={{ ...liveRow, marginTop: 10 }}>
                          <div style={{ flex: "1 1 220px" }}>
                            <label style={liveLabel}>Skills</label>
                            <input type="text" style={{ ...inputStyle(theme), width: "100%" }} value={ghSkills} onChange={(e) => setGhSkills(e.target.value)} placeholder="Skills" />
                          </div>
                        </div>
                      )}
                      <div style={{ ...liveRow, marginTop: 12 }}>
                        <Button theme={theme} onClick={saveGitHubCandidate} disabled={ghSaveBusy}>
                          {ghSaveBusy ? "Adding..." : "Add to list"}
                        </Button>
                        <Button theme={theme} variant="ghost" onClick={searchSavedLiveCandidates}>
                          Show saved
                        </Button>
                      </div>
                    </div>

                    {(error || ghPullMessage) && platformFilter === "github" && (
                      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 12, color: error ? "#EF4444" : theme.accent }}>
                        {error || ghPullMessage}
                      </div>
                    )}
                    {githubSourced.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>Added candidates ({githubSourced.length})</div>
                          <div style={{ fontSize: 11, color: theme.textMute }}>Check a box to save into Searched Candidate by Recruiter</div>
                        </div>
                        <div style={{ display: "grid", gap: 8 }}>
                        {githubSourced.slice(0, 10).map((c, idx) => {
                          const key = getCandidateKey(c);
                          const isChecked = selectedCandidateKeys.has(key);
                          const rawGh = String(c.profileUrl || c.github_url || "");
                          const isGhProfile = /github\.com\/[^/?#]+\/?$/i.test(rawGh) && !/github\.com\/search/i.test(rawGh);
                          const ghUrl = isGhProfile ? rawGh : githubPeopleSearchUrl(c);
                          return (
                            <div
                              key={`${key}-${idx}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 10,
                                padding: "10px 12px",
                                borderRadius: 10,
                                background: isChecked ? `${theme.accent}12` : theme.bg,
                                border: `1px solid ${isChecked ? theme.accent : theme.line}`
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleCandidateSelect(c)}
                                  style={{ width: 16, height: 16, cursor: "pointer", accentColor: theme.accent }}
                                />
                                {c.avatar_url && (
                                  <img src={c.avatar_url} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} />
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    toggleCandidateSelect(c);
                                    if (c.github_username) setGhUsername(c.github_username);
                                    if (c.github_url || c.profileUrl) setGhProfileUrl(c.github_url || c.profileUrl);
                                    if (c.firstName) setGhFirstName(c.firstName);
                                    if (c.lastName) setGhLastName(c.lastName);
                                    if (c.email) setGhEmail(c.email);
                                    if (c.phone) setGhPhone(c.phone);
                                    if (c.location) setGhLocation(c.location);
                                    if (c.current_company || c.company) setGhCompany(c.current_company || c.company);
                                    if (c.job_title || c.title || c.role) setGhJobTitle(c.job_title || c.title || c.role);
                                    if (c.bio || c.profile_summary) setGhBio(c.bio || c.profile_summary);
                                  }}
                                  style={{ background: "transparent", border: "none", textAlign: "left", cursor: "pointer", flex: 1, padding: 0, color: theme.text }}
                                >
                                  <span style={{ fontWeight: 800, fontSize: 13 }}>{displayPersonName(c) || c.name}</span>
                                  <div style={{ fontSize: 12, color: theme.textMute, marginTop: 2 }}>
                                    {[c.job_title || c.title || c.role, c.company || c.current_company, c.location].filter(Boolean).join(" · ")}
                                  </div>
                                  {(c.email || c.phone) && (
                                    <div style={{ fontSize: 11, color: theme.textMute, marginTop: 2 }}>
                                      {[c.email, c.phone].filter(Boolean).join(" · ")}
                                    </div>
                                  )}
                                </button>
                              </div>
                              <a
                                href={ghUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => { e.stopPropagation(); window.open(ghUrl, "_blank", "noopener,noreferrer"); }}
                                style={{ fontSize: 11, color: theme.accent, whiteSpace: "nowrap", fontWeight: 700, cursor: "pointer" }}
                              >
                                Open profile
                              </a>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {platformFilter !== "linkedin" && platformFilter !== "github" && (
                  <div style={{ marginBottom: 14, padding: 14, borderRadius: 10, border: `1px dashed ${theme.line}`, background: theme.bg }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="badge bg-warning text-dark fw-bold" style={{ fontSize: 10 }}>UPCOMING INTEGRATION</span>
                      <strong style={{ fontSize: 13, color: theme.text }}>
                        {platformFilter === "naukri" ? "Naukri" : platformFilter === "glassdoor" ? "Glassdoor" : platformFilter === "formdin" ? "Foundit / Formdin" : "Indeed"}
                      </strong>
                    </div>
                    <div style={{ fontSize: 12, color: theme.textMute, marginTop: 4 }}>
                      Direct candidate search integration for this platform is upcoming. Active candidate search is available on the LinkedIn &amp; GitHub tabs.
                    </div>
                  </div>
                )}
              </div>

              {/* Searched Candidate by Recruiter Table */}
              <div ref={matchingCandidatesRef} id="matching-candidates" style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 12, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                      Searched Candidate by Recruiter ({selectedSearchedCandidates.length})
                    </h3>
                    <p style={{ fontSize: 12, color: theme.textMute, margin: 0 }}>
                      Candidates you add from LinkedIn or GitHub appear here.
                    </p>
                  </div>
                  {selectedSearchedCandidates.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSearchedCandidates([]);
                        setSelectedCandidateKeys(new Set());
                      }}
                      style={{
                        background: "transparent",
                        border: `1px solid ${theme.line}`,
                        color: theme.textMute,
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontSize: 11,
                        cursor: "pointer"
                      }}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>

                {selectedSearchedCandidates.length === 0 ? (
                  <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 8, padding: 24, color: theme.textMute, fontSize: 13, textAlign: "center" }}>
                    No candidates yet. Add a contact above, then <strong>check the box</strong> in Added candidates to save them here.
                  </div>
                ) : (
                    <div style={{ overflowX: "auto", border: `1px solid ${theme.line}`, borderRadius: 10 }}>
                    <table style={{ width: "100%", minWidth: 1040, borderCollapse: "collapse", fontSize: 13, border: `1px solid ${theme.line}` }}>
                      <thead>
                        <tr style={{ background: theme.bg, textAlign: "left" }}>
                          {["Profile Photo", "Candidate Name", "Email", "Phone", "Role", "Company Name", "Location", "Source", "Action"].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "10px 12px",
                                fontSize: 11,
                                fontWeight: 700,
                                color: theme.textMute,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                border: `1px solid ${theme.line}`,
                                whiteSpace: "nowrap"
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSearchedCandidates.map((c, idx) => {
                          const photo = candidateDisplayPhotoSrc(c) || c.avatar_url;
                          const source = candidateSourceLabel(c);
                          const isReg = isWibilRegistered(c);
                          const candRole = c.job_title || c.jobTitle || c.role || c.title || c.job_role || "-";
                          const candCompany = displayCompanyName(c) || c.company || c.current_company || c.company_name || "-";
                          const candEmail = c.email || "-";
                          const candPhone = c.phone || c.phoneNumber || c.phone_number || "-";
                          const candLocation = c.location || "-";

                          return (
                            <tr
                              key={`${c.id || "cand"}-${c.name}-${idx}`}
                              style={{ background: idx % 2 === 0 ? "transparent" : theme.bg }}
                            >
                              <td style={{ padding: "10px 12px", verticalAlign: "middle", width: 64, border: `1px solid ${theme.line}` }}>
                                <div style={{ position: "relative", width: 42, height: 42 }}>
                                  <div style={{
                                    width: 42, height: 42, borderRadius: "50%",
                                    background: theme.accentDim, color: theme.accent,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontWeight: 700, fontSize: 13, border: `1px solid ${theme.line}`
                                  }}>
                                    {candidateInitials(c)}
                                  </div>
                                  {photo ? (
                                    <img
                                      src={photo}
                                      alt=""
                                      referrerPolicy="no-referrer"
                                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                                      style={{
                                        position: "absolute", inset: 0, width: 42, height: 42,
                                        borderRadius: "50%", objectFit: "cover",
                                        border: `1px solid ${theme.line}`, display: "block", background: theme.bg
                                      }}
                                    />
                                  ) : null}
                                </div>
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", fontWeight: 700, minWidth: 140, border: `1px solid ${theme.line}` }}>
                                <div style={{ color: theme.text }}>{displayPersonName(c) || c.name || "Candidate"}</div>
                                {isReg && (
                                  <span style={{ fontSize: 10, color: "#10B981", fontWeight: 700, background: "rgba(16,185,129,0.12)", padding: "1px 6px", borderRadius: 4, marginTop: 2, display: "inline-block" }}>
                                    WIBIL Verified
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", color: theme.text, fontWeight: 500, minWidth: 160, border: `1px solid ${theme.line}`, wordBreak: "break-all" }}>
                                {candEmail}
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", color: theme.text, fontWeight: 500, minWidth: 120, border: `1px solid ${theme.line}`, whiteSpace: "nowrap" }}>
                                {candPhone}
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", color: theme.text, fontWeight: 500, minWidth: 140, border: `1px solid ${theme.line}` }}>
                                {isPlaceholderCompanyLabel(candRole) ? "-" : candRole}
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", color: theme.textMute, minWidth: 140, border: `1px solid ${theme.line}` }}>
                                {candCompany}
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", color: theme.textMute, minWidth: 120, border: `1px solid ${theme.line}` }}>
                                {candLocation}
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", whiteSpace: "nowrap", border: `1px solid ${theme.line}` }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 6,
                                  background: source === "LinkedIn" ? "rgba(10,102,194,0.15)" : (source === "GitHub" ? "rgba(34,197,94,0.15)" : theme.accentDim),
                                  color: source === "LinkedIn" ? "#0A66C2" : (source === "GitHub" ? "#16A34A" : theme.accent)
                                }}>
                                  {source}
                                </span>
                              </td>
                              <td style={{ padding: "12px", verticalAlign: "middle", border: `1px solid ${theme.line}`, textAlign: "center" }}>
                                <Button
                                  theme={theme}
                                  onClick={() => openCandidateProfile(c)}
                                  style={{ padding: "6px 16px", fontSize: 12, fontWeight: 700 }}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Interview Feedbacks & Hires Tracker */}
              <div style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 12, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, cursor: "pointer" }} onClick={() => setShowShortlistedModal(true)}>
                    Shortlisted Candidates & Interviews ({shortlistItems.length})
                  </h3>
                  <Button theme={{ ...theme, surface: theme.line }} onClick={() => setShowShortlistedModal(true)} style={{ padding: "4px 10px", fontSize: 11, background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
                    <Eye size={12} /> View Full List
                  </Button>
                </div>

                {shortlistItems.length === 0 ? (
                  <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 8, padding: 20, color: theme.textMute, fontSize: 13, textAlign: "center" }}>
                    No candidates shortlisted yet.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {shortlistItems.map((rc, idx) => (
                      <div key={rc.link_id || rc.id || rc.candidate_name || `cand-sl-${idx}`} style={{ border: `1px solid ${theme.line}`, borderRadius: 8, padding: 12, fontSize: 12.5 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <strong>{displayPersonName(rc) || rc.candidate_name || rc.name || "Candidate"}</strong>
                            <div style={{ fontSize: 11, color: theme.textMute }}>For: {rc.job_role || rc.role || "Software Position"}</div>
                          </div>
                          <div style={{
                            fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px",
                            background: (rc.status || "").toLowerCase() === "hired" ? "rgba(34,197,94,0.1)" : ((rc.status || "").toLowerCase() === "rejected" ? "rgba(239,68,68,0.1)" : "rgba(255,210,76,0.1)"),
                            color: (rc.status || "").toLowerCase() === "hired" ? "#22c55e" : ((rc.status || "").toLowerCase() === "rejected" ? "#ef4444" : theme.accent)
                          }}>
                            {(rc.status || "SHORTLISTED").toUpperCase()}
                          </div>
                        </div>

                        <div style={{ fontSize: 11, color: theme.textMute, marginTop: 4 }}>
                          <strong>Interview Date:</strong> {rc.interview_date || "Not scheduled"}
                        </div>
                        <div style={{ fontSize: 11, color: theme.textMute }}>
                          <strong>AI Resume Match:</strong> {rc.match_score != null ? `${rc.match_score}%` : " - "}
                        </div>

                        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                          {rc.employee_id && (
                            <Button theme={theme} onClick={() => viewCandidateDetails({ id: rc.employee_id, employeeId: rc.employee_id, name: rc.candidate_name, title: rc.job_role, uan: rc.candidate_uan })} style={{ flex: 1, justifyContent: "center", padding: "4px 0", fontSize: 11 }}>
                              View Details
                            </Button>
                          )}
                          {(rc.status || "").toLowerCase() === "shortlisted" || !rc.status ? (
                            <Button theme={{ ...theme, surface: theme.line }} onClick={() => { setSelectedLink(rc); setFeedbackStatus("hired"); setFeedbackText(rc.feedback || ""); setFeedbackRating(rc.rating || 5); }} style={{ flex: 1, justifyContent: "center", padding: "4px 0", fontSize: 11, background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
                              Record Feedback
                            </Button>
                          ) : (
                            <Button theme={{ ...theme, surface: theme.line }} onClick={() => { setSelectedLink(rc); setFeedbackStatus(rc.status || "hired"); setFeedbackText(rc.feedback || ""); setFeedbackRating(rc.rating || 5); }} style={{ flex: 1, justifyContent: "center", padding: "4px 0", fontSize: 11, background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
                              Update Decision
                            </Button>
                          )}
                        </div>

                        {rc.feedback && (
                          <div style={{ background: theme.bg, borderRadius: 6, padding: 8, marginTop: 8 }}>
                            <div><strong>Feedback:</strong> {rc.feedback}</div>
                            <div><strong>Rating Given:</strong> {rc.rating}/10</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>


          </div>
        </div>

        {/* Browseract AI Web Scraper Modal */}
        {showAiScraperModal && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.75)", zIndex: 9999, display: "flex",
            alignItems: "center", justifyContent: "center", padding: 20
          }}>
            <div style={{
              background: theme.surface, border: `1px solid ${theme.accent}`,
              borderRadius: 16, maxWidth: 650, width: "100%", maxHeight: "90vh",
              overflowY: "auto", padding: 24, boxShadow: "0 20px 50px rgba(0,0,0,0.5)"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Brain color={theme.accent} size={24} />
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.text }}>
                    Browseract AI Web Scraper Agent
                  </h3>
                </div>
                <button type="button" onClick={() => setShowAiScraperModal(false)} style={{ background: "transparent", border: "none", color: theme.textMute, fontSize: 22, cursor: "pointer" }}>x</button>
              </div>

              <p style={{ fontSize: 13, color: theme.textMute, marginBottom: 20, lineHeight: 1.5 }}>
                Live AI agent: paste any public <strong>Target URL</strong> and an <strong>AI Prompt</strong>. The agent scrapes the page, returns structured JSON with an AI confidence score, and auto-ingests into the recruiter database. Dummy people are never invented. LinkedIn jobs use the LinkedIn Job Scraper button.
              </p>

              <form onSubmit={runAiWebScraper}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6, display: "block" }}>
                    Target URL
                  </label>
                  <input
                    type="url"
                    required
                    placeholder="https://github.com"
                    style={{ ...inputStyle(theme), width: "100%" }}
                    value={aiScraperUrl}
                    onChange={(e) => setAiScraperUrl(e.target.value)}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6, display: "block" }}>
                    AI Prompt
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Extract structured candidate or page details: name, role, company, location, skills"
                    style={{ ...inputStyle(theme), width: "100%", fontFamily: "inherit" }}
                    value={aiScraperPrompt}
                    onChange={(e) => setAiScraperPrompt(e.target.value)}
                  />
                </div>

                <Button
                  type="submit"
                  theme={theme}
                  disabled={aiScraperBusy}
                  style={{ width: "100%", justifyContent: "center", padding: "12px", fontWeight: 700 }}
                >
                  {aiScraperBusy ? "AI Agent Scraping Website..." : "🚀 Run AI Web Scraper Agent"}
                </Button>
              </form>

              {aiScraperResult && (
                <div style={{ marginTop: 24, borderTop: `1px solid ${theme.line}`, paddingTop: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: theme.accent }}>
                      Live results ({(aiScraperResult.records || []).length}){aiScraperResult.source ? `  ·  ${aiScraperResult.source}` : ""}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: theme.text, background: theme.accentDim, padding: "3px 8px", borderRadius: 8 }}>
                      Confidence {aiScraperResult.confidenceScore != null ? `${aiScraperResult.confidenceScore}%` : "n/a"}
                      {aiScraperResult.browseractConfigured === false ? "  ·  Browseract not configured" : ""}
                      {aiScraperResult.browseractConfigured === true ? "  ·  Browseract" : ""}
                    </span>
                  </div>

                  {aiScraperResult.ingestNotice && (
                    <div style={{ fontSize: 12, color: "#10B981", marginBottom: 10, fontWeight: 600 }}>
                      {aiScraperResult.ingestNotice}
                    </div>
                  )}

                  {aiScraperResult.extractedData && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMute, marginBottom: 6 }}>Structured JSON</div>
                      <pre style={{
                        margin: 0, fontSize: 11, lineHeight: 1.45, maxHeight: 220, overflow: "auto",
                        background: theme.bg, border: `1px solid ${theme.line}`, borderRadius: 8, padding: 12,
                        color: theme.text, whiteSpace: "pre-wrap", wordBreak: "break-word"
                      }}>
                        {JSON.stringify(aiScraperResult.extractedData, null, 2)}
                      </pre>
                    </div>
                  )}

                  {aiScraperResult.error && !(aiScraperResult.records || []).length && !aiScraperResult.extractedData ? (
                    <div style={{ border: `1px solid rgba(239,68,68,0.35)`, background: "rgba(239,68,68,0.08)", borderRadius: 8, padding: 16, color: "#FCA5A5", fontSize: 13 }}>
                      {aiScraperResult.error}
                    </div>
                  ) : (aiScraperResult.records || []).length === 0 && !aiScraperResult.extractedData ? (
                    <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 8, padding: 16, color: theme.textMute, fontSize: 13, textAlign: "center" }}>
                      No public structured data found. Dummy data is not shown.
                    </div>
                  ) : (aiScraperResult.records || []).length === 0 && aiScraperResult.extractedData ? (
                    <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 8, padding: 12, color: theme.textMute, fontSize: 12, textAlign: "center" }}>
                      Page extract only — no candidate people were invented.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {(aiScraperResult.records || []).map((c, idx) => (
                        <div key={`${c.id || "sc"}-${idx}`} style={{
                          background: theme.bg, border: `1px solid ${theme.line}`,
                          borderRadius: 10, padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center"
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              {displayPersonName(c)}
                              <span style={{ fontSize: 10, background: "rgba(34,197,94,0.15)", color: "#22c55e", border: "1px solid #22c55e", padding: "1px 8px", borderRadius: 10, fontWeight: 700 }}>
                                {c.badgeText || c.platform || "Live"}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: theme.textMute, marginTop: 2 }}>
                              {isPlaceholderCompanyLabel(c.title) ? "" : (c.title || "")}{displayCompanyName(c) ? ` at ${displayCompanyName(c)}` : ""}{c.location ? `  ·  ${c.location}` : ""}
                            </div>
                            {(c.postedTime || c.contractType || c.experienceLevel || c.salary) && (
                              <div style={{ fontSize: 11, color: theme.textMute, marginTop: 2 }}>
                                {[c.postedTime && `Posted ${c.postedTime}`, c.contractType, c.experienceLevel, c.salary].filter(Boolean).join("  ·  ")}
                              </div>
                            )}
                            <div style={{ fontSize: 10.5, color: theme.accent, background: theme.accentDim, padding: "3px 8px", borderRadius: 4, display: "inline-block", marginTop: 4 }}>
                              Skills: {c.strengths || (Array.isArray(c.skills) ? c.skills.join(", ") : "") || " - "}
                            </div>
                            {c.summary && (
                              <div style={{ fontSize: 11, color: theme.textMute, marginTop: 6, lineHeight: 1.4 }}>{c.summary}</div>
                            )}
                            {(c.applyUrl || c.profileUrl || c.name) && (
                              <div style={{ marginTop: 6 }}>
                                <a href={candidatePublicLink(c).href} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: theme.accent }}>
                                  {/job/i.test(c.badgeText || c.source || "") ? "Open job posting" : candidatePublicLink(c).label}
                                </a>
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: "right", marginLeft: 12 }}>
                            <Button theme={theme} onClick={() => {
                              viewCandidateDetails(c);
                              setShowAiScraperModal(false);
                            }} style={{ padding: "4px 10px", fontSize: 11 }}>
                              {/job/i.test(c.badgeText || c.source || "") ? "View job" : "View candidate"}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {aiScraperResult.savedRecord && (
                    <div style={{ fontSize: 12, color: "#10B981", marginTop: 10, fontWeight: 600 }}>
                      {aiScraperResult.ingestNotice || "Auto-ingested into the recruiter database (live scrape, not dummy)."}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {showLiJobsModal && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.75)", zIndex: 9999, display: "flex",
            alignItems: "center", justifyContent: "center", padding: 20
          }}>
            <div style={{
              background: theme.surface, border: `1px solid ${theme.accent}`,
              borderRadius: 16, maxWidth: 650, width: "100%", maxHeight: "90vh",
              overflowY: "auto", padding: 24, boxShadow: "0 20px 50px rgba(0,0,0,0.5)"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Briefcase color={theme.accent} size={24} />
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.text }}>
                    LinkedIn Job Scraper
                  </h3>
                </div>
                <button type="button" onClick={() => setShowLiJobsModal(false)} style={{ background: "transparent", border: "none", color: theme.textMute, fontSize: 22, cursor: "pointer" }}>x</button>
              </div>
              <p style={{ fontSize: 13, color: theme.textMute, marginBottom: 20, lineHeight: 1.5 }}>
                Public LinkedIn guest job cards (or Apify if configured). This does not log into LinkedIn accounts. Jobs are not turned into fake candidate people.
              </p>
              <form onSubmit={runLinkedInJobScraper}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6, display: "block" }}>Keywords</label>
                  <input
                    type="text"
                    required
                    placeholder="Software Engineer"
                    style={{ ...inputStyle(theme), width: "100%" }}
                    value={liJobsKeywords}
                    onChange={(e) => setLiJobsKeywords(e.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6, display: "block" }}>Location</label>
                  <input
                    type="text"
                    placeholder="India"
                    style={{ ...inputStyle(theme), width: "100%" }}
                    value={liJobsLocation}
                    onChange={(e) => setLiJobsLocation(e.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: theme.text, marginBottom: 6, display: "block" }}>Limit</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    style={{ ...inputStyle(theme), width: "100%" }}
                    value={liJobsLimit}
                    onChange={(e) => setLiJobsLimit(e.target.value)}
                  />
                </div>
                <Button type="submit" theme={theme} disabled={liJobsBusy} style={{ width: "100%", justifyContent: "center", padding: "12px", fontWeight: 700 }}>
                  {liJobsBusy ? "Scraping LinkedIn jobs..." : "Run LinkedIn Job Scraper"}
                </Button>
              </form>
              {liJobsResult && (
                <div style={{ marginTop: 24, borderTop: `1px solid ${theme.line}`, paddingTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, marginBottom: 8 }}>
                    {(liJobsResult.jobs || []).length} job(s)  ·  {liJobsResult.methodUsed || "live"}
                    {liJobsResult.apifyConfigured === false ? "  ·  Apify not configured" : ""}
                  </div>
                  {liJobsResult.ingestNotice && (
                    <div style={{ fontSize: 12, color: "#10B981", marginBottom: 10, fontWeight: 600 }}>{liJobsResult.ingestNotice}</div>
                  )}
                  {liJobsResult.error && !(liJobsResult.jobs || []).length && (
                    <div style={{ border: `1px solid rgba(239,68,68,0.35)`, background: "rgba(239,68,68,0.08)", borderRadius: 8, padding: 12, color: "#FCA5A5", fontSize: 13, marginBottom: 10 }}>
                      {liJobsResult.error}
                    </div>
                  )}
                  <pre style={{
                    margin: 0, fontSize: 11, lineHeight: 1.45, maxHeight: 280, overflow: "auto",
                    background: theme.bg, border: `1px solid ${theme.line}`, borderRadius: 8, padding: 12,
                    color: theme.text, whiteSpace: "pre-wrap", wordBreak: "break-word"
                  }}>
                    {JSON.stringify({
                      success: liJobsResult.success,
                      dummy: liJobsResult.dummy,
                      configured: liJobsResult.configured,
                      apifyConfigured: liJobsResult.apifyConfigured,
                      methodUsed: liJobsResult.methodUsed,
                      stats: liJobsResult.stats,
                      jobs: liJobsResult.jobs || []
                    }, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post Job Modal */}
        <PostJobModal
          isOpen={showJobModal}
          theme={theme}
          fonts={fonts}
          jobPostStep={jobPostStep}
          setJobPostStep={setJobPostStep}
          jobForm={jobForm}
          setJobForm={setJobForm}
          jobFormErrors={jobFormErrors}
          setJobFormErrors={setJobFormErrors}
          boardPublishQueue={boardPublishQueue}
          boardPublishIndex={boardPublishIndex}
          boardLinkJob={boardLinkJob}
          boardLinkUrl={boardLinkUrl}
          setBoardLinkUrl={setBoardLinkUrl}
          boardInterestAsk={boardInterestAsk}
          busy={busy}
          onJobFormSubmit={onJobFormSubmit}
          toggleJobPlatform={toggleJobPlatform}
          openOneBoardWindow={openOneBoardWindow}
          goToNextBoard={goToNextBoard}
          confirmBoardInterest={confirmBoardInterest}
          closeJobModal={closeJobModal}
        />

        {/* Interview Feedback Modal */}
        <InterviewFeedbackModal
          selectedCandidate={selectedLink}
          feedbackStatus={feedbackStatus}
          setFeedbackStatus={setFeedbackStatus}
          feedbackText={feedbackText}
          setFeedbackText={setFeedbackText}
          feedbackRating={feedbackRating}
          setFeedbackRating={setFeedbackRating}
          busy={busy}
          theme={theme}
          fonts={fonts}
          onClose={() => setSelectedLink(null)}
          onSubmit={submitFeedback}
        />

        {/* Posted Jobs & Candidate Applications Modal */}
        <JobsListModal
          isOpen={showJobsListModal}
          jobs={jobs}
          theme={theme}
          fonts={fonts}
          onClose={() => setShowJobsListModal(false)}
          onOpenJobModal={() => openJobModal()}
          onSelectJobForScreening={(jobId) => setTargetJobIdScreen(jobId)}
          onSelectJobForShortlisting={(jobId) => setTargetJobId(jobId)}
          onOpenBoardToPost={(job, platform) => openBoardToPost(job, platform)}
        />

        {/* Shortlisted Candidates & Interviews Modal */}
        <ShortlistedCandidatesModal
          isOpen={showShortlistedModal}
          shortlistItems={shortlistItems}
          hiredCandidates={hiredCandidates}
          shortlistModalFilter={shortlistModalFilter}
          setShortlistModalFilter={setShortlistModalFilter}
          whatsAppMsgStatus={whatsAppMsgStatus}
          theme={theme}
          fonts={fonts}
          onClose={() => setShowShortlistedModal(false)}
          onViewCandidateDetails={viewCandidateDetails}
          onOpenFeedbackModal={(rc) => {
            setSelectedLink(rc);
            setFeedbackStatus(rc.status || "hired");
            setFeedbackText(rc.feedback || "");
            setFeedbackRating(rc.rating || 5);
          }}
          onSendWhatsApp={handleSendWhatsApp}
        />
      </div>
    </div>
  );
}
